/**
 * Transaction Helper
 * Core utilities for managing transactions in the unified architecture
 */

import {
  collection,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  runTransaction,
  writeBatch,
  arrayUnion
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { writeAuditLog } from './validationHelpers';
import { startSaveTimer, timed } from './saveTimer';
import { TRAN_TYPES, getTranType, affectsStock, affectsAccounting } from '../config/tranTypes';
import { generateSubTransactions, getSubTransactions, normalizeLedgerStatus, listSubTransactionIds, prewarmSystemAccounts } from './subTransactionHelper';
import { assertLocationsUnlocked, getLocationLocks } from './stockAuditHelper';
import { generateStockFlow, getStockFlow, listStockFlowRows, applyEntriesToStockCache, invalidateItemStock, getBranchLocation } from './stockFlowHelper';
import { loadInventoryMovementConfig, shouldPostStock } from './inventoryMovementHelper';
import { beginBusy, endBusy } from './busyBus';
import { enrichLineWithUnit, twoUnitsModuleOn, twoUnitLineFields, roundBaseQty } from './unitConversion';
import { recordReads } from './readTelemetry';
import { getItemsCached, getAccountsCached } from './masterDataStore';
import { prepareStockCosting, costRowsChange } from './itemCostEngine';

// ─── Post-permission checker (set by AuthProvider on mount) ──────────────────
// Allows the helper to enforce the Post/Approve permission without coupling
// to React context directly. Default permissive (admin/owner bypass anyway).
let _canPostChecker = () => true;
export const setCanPostChecker = (fn) => { _canPostChecker = typeof fn === 'function' ? fn : () => true; };

// Map tran type code → permission category/key used in roles matrix.
const TRAN_TYPE_PERMISSION = {
  PI: { cat: 'transactions', key: 'purchaseInvoice' },
  NPI: { cat: 'transactions', key: 'nonTaxPurchaseInvoice' },
  IPI: { cat: 'transactions', key: 'importPurchaseInvoice' },
  PR: { cat: 'transactions', key: 'purchaseReturn' },
  NPR: { cat: 'transactions', key: 'nonTaxPurchaseReturn' },
  PO: { cat: 'transactions', key: 'purchaseOrder' },
  GRN: { cat: 'transactions', key: 'grn' },
  SI: { cat: 'transactions', key: 'saleInvoice' },
  NSI: { cat: 'transactions', key: 'nonTaxSaleInvoice' },
  SR: { cat: 'transactions', key: 'saleReturn' },
  NSR: { cat: 'transactions', key: 'nonTaxSaleReturn' },
  SO: { cat: 'transactions', key: 'saleOrder' },
  QT: { cat: 'transactions', key: 'quotation' },
  DC: { cat: 'transactions', key: 'deliveryChallan' },
  CR: { cat: 'transactions', key: 'cashReceipt' },
  BR: { cat: 'transactions', key: 'bankReceipt' },
  CP: { cat: 'transactions', key: 'cashPayment' },
  BP: { cat: 'transactions', key: 'bankPayment' },
  JV: { cat: 'transactions', key: 'journalVoucher' },
  AS: { cat: 'transactions', key: 'addStock' },
  RS: { cat: 'transactions', key: 'reduceStock' },
  ST: { cat: 'transactions', key: 'stockTransfer' },
  PS: { cat: 'transactions', key: 'posCounterSale' },
  // Store
  REQ: { cat: 'store', key: 'purchaseRequisition' },
  IGP: { cat: 'store', key: 'inwardGatePass' },
  OGP: { cat: 'store', key: 'outwardGatePass' },
  BOM: { cat: 'store', key: 'billOfMaterials' },
  PRO: { cat: 'store', key: 'productionOrder' },
};

// Active (non-Draft) statuses that require post permission.
const POSTING_STATUSES = new Set(['Posted', 'Approved', 'Received', 'Dispatched', 'Delivered', 'Completed', 'InProgress', 'Partial', 'Paid', 'Cancelled', 'Sent']);

const assertCanPost = (tranTypeCode, status) => {
  if (!status || status === 'Draft') return;
  if (!POSTING_STATUSES.has(status)) return;
  const perm = TRAN_TYPE_PERMISSION[tranTypeCode];
  if (!perm) return;
  if (!_canPostChecker(perm.cat, perm.key)) {
    const verb = status === 'Cancelled' ? 'cancel/void' : 'post';
    const err = new Error(`You do not have permission to ${verb} this document. Save as Draft instead and ask a supervisor to ${verb} it.`);
    err.code = 'POST_PERMISSION_DENIED';
    throw err;
  }
};

/**
 * Enrich transaction lines with multi-unit fields (baseQuantity, baseUnitCost,
 * baseUnitPrice, conversion). Fetches each unique itemId once and applies
 * enrichLineWithUnit() per line. For items without `units[]` (legacy single-unit),
 * baseQuantity = quantity, baseUnitCost = unitCost — fully backward compatible.
 *
 * Called from createTransaction / updateTransaction before stockFlow generation
 * so the canonical inventory layer always reads base-unit values.
 */
export const enrichTransactionLines = async (companyId, lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return lines;
  const itemIds = [...new Set(lines.map(l => l && l.itemId).filter(Boolean))];
  if (itemIds.length === 0) return lines;

  // Firebase cost: resolve item records from the device-cached items (0 reads)
  // instead of a getDoc per line at save time. Any item not in the cache (rare)
  // is read directly so enrichment is never incomplete.
  const itemMap = {};
  try {
    const all = await getItemsCached(companyId);
    const need = new Set(itemIds);
    all.forEach(it => { if (need.has(it.id)) itemMap[it.id] = it; });
  } catch (e) { /* fall through to direct reads */ }
  const missing = itemIds.filter(id => !itemMap[id]);
  if (missing.length) {
    await Promise.all(missing.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId, 'inventoryItems', id));
        if (snap.exists()) itemMap[id] = { id, ...snap.data() };
      } catch (e) { /* ignore — line stays single-unit */ }
    }));
  }

  return lines.map(line => {
    if (!line || !line.itemId) return line;
    const item = itemMap[line.itemId];
    if (!item) return line;
    const enriched = enrichLineWithUnit(item, line);
    // Tag service vs. product so downstream helpers (stockFlow, COGS) can skip
    // service lines without re-fetching the item.
    enriched.itemType = item.itemType === 'service' ? 'service' : 'product';
    // Show Two Units: save the same qty in the item's second unit so prints and
    // views can show both (e.g. "10 Ctn (240 Mtr)"). Display only. The line's
    // base qty is cleaned of conversion leftovers (250.000000008 → 250).
    // Module off / no pair: any old second-unit fields are dropped so they can
    // never go stale (no-op for companies that never used the module).
    const two = twoUnitsModuleOn() ? twoUnitLineFields(item, enriched) : null;
    if (two) {
      Object.assign(enriched, two);
      enriched.baseQuantity = roundBaseQty(enriched.baseQuantity);
    } else if ('secondUnitCode' in enriched || 'secondQuantity' in enriched) {
      delete enriched.secondUnitCode; delete enriched.secondQuantity;
    }
    return enriched;
  });
};

/**
 * Remove undefined values from an object (Firestore rejects undefined)
 * Handles nested objects and arrays recursively
 */
const stripUndefined = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(item => (item && typeof item === 'object') ? stripUndefined(item) : item);
  }
  if (obj && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, (v && typeof v === 'object' && !(v instanceof Date)) ? stripUndefined(v) : v])
    );
  }
  return obj;
};

// Map each tranTypeCode to the Document Settings UI key so we can read
// user-configured prefix / padding / startNumber from settings/documentNumbering.
const TRAN_CODE_TO_DOC_KEY = {
  PO: 'purchaseOrder', GRN: 'grn', PI: 'purchaseInvoice', PR: 'purchaseReturn',
  NPI: 'nonTaxPurchaseInvoice', NPR: 'nonTaxPurchaseReturn', IPI: 'importPurchaseInvoice',
  QT: 'quotation', SO: 'saleOrder', DC: 'deliveryChallan',
  SI: 'saleInvoice', SR: 'saleReturn', NSI: 'nonTaxSaleInvoice', NSR: 'nonTaxSaleReturn',
  PS: 'posCounterSale',
  CR: 'cashReceipt', BR: 'bankReceipt', CP: 'cashPayment', BP: 'bankPayment',
  JV: 'journalVoucher', CB: 'cashBook',
  REQ: 'purchaseRequisition', IGP: 'inwardGatePass', OGP: 'outwardGatePass',
  AS: 'addStock', RS: 'reduceStock', ST: 'stockTransfer', BT: 'branchTransfer',
  BOM: 'billOfMaterials', PRO: 'productionOrder',
  ADV: 'salaryAdvance', LON: 'staffLoan', PAY: 'payrollRun', FNF: 'finalSettlement',
  LRC: 'loanRecovery'
};

/**
 * Generate document number for a transaction
 * Format: {PREFIX}{YEAR}-{NUMBER:padding}
 * Reads custom prefix / padding / startNumber from settings/documentNumbering if the
 * user configured them via Settings → Document Settings. Falls back to the tranType default.
 * @param {string} [branchId] - Optional branch ID for branch-wise counters
 */
// Parse the trailing integer from a docNumber like "SI-2024-00010" → 10.
// Returns 0 when unparseable. Always splits on '-' and takes the last segment,
// so custom prefixes ("INVOICE-", "2024-25-") still work because the number
// is always the last hyphen-delimited token.
const parseDocSeq = (docNumber) => {
  if (!docNumber || typeof docNumber !== 'string') return 0;
  const tail = docNumber.split('-').pop();
  const n = parseInt(tail, 10);
  return Number.isFinite(n) ? n : 0;
};

// Read existing transactions of (type, branch, financial-year) and return the
// highest sequence number found. One-shot seed for a brand-new scope counter
// so new-format numbers continue from any documents already in that scope
// (this is what makes the format switch "new documents only"). Runs at most
// once per (type, branch, FY) — once the scope counter exists, this is skipped.
const scanMaxForScope = async (companyId, tranTypeCode, branchId, financialYearId, fallbackYear) => {
  try {
    const txnRef = collection(db, 'companies', companyId, 'transactions');
    const q = query(txnRef, where('tranTypeCode', '==', tranTypeCode));
    const snap = await getDocs(q);
    let max = 0;
    snap.docs.forEach(d => {
      const data = d.data();
      // Match branch (null vs specific) so per-branch counters stay isolated.
      if ((data.branchId || null) !== (branchId || null)) return;
      // Scope by financial year. Legacy docs missing financialYearId fall back
      // to a calendar-year match so their sequence is still respected.
      if (financialYearId) {
        if (data.financialYearId) {
          if (data.financialYearId !== financialYearId) return;
        } else if (!data.docDate || new Date(data.docDate).getFullYear() !== fallbackYear) {
          return;
        }
      }
      const n = parseDocSeq(data.docNumber);
      if (n > max) max = n;
    });
    return max;
  } catch (err) {
    console.warn('[doc#] scope scan failed (defaulting to 0):', err?.message);
    return 0;
  }
};

// Firebase cost: the numbering step below reads 3 low-churn docs on EVERY save —
// the documentNumbering settings, the financial year, and the branch. They change
// rarely, so we cache them per device (30-min TTL). This removes ~3 sequential
// round-trips from every document save (the measured save bottleneck). A settings
// / FY / branch edit reflects within the TTL, or immediately after a refresh.
const _numCache = new Map(); // key -> { at, value }
const _NUM_TTL_MS = 30 * 60 * 1000;
const _numCacheGet = (key) => {
  const h = _numCache.get(key);
  return (h && Date.now() - h.at < _NUM_TTL_MS) ? h.value : undefined;
};
const _numCacheSet = (key, value) => { _numCache.set(key, { at: Date.now(), value }); };

// The financial-year doc is needed twice on every save — once to validate the
// date, once to build the document number. They used to cache it under two
// DIFFERENT keys (`fyval|` and `fy|`), so the same document was read twice on
// the first save of every session. One key, one read.
const _getFinancialYearCached = async (companyId, financialYearId) => {
  const key = `fy|${companyId}|${financialYearId}`;
  let fy = _numCacheGet(key);
  if (fy === undefined) {
    const snap = await getDoc(doc(db, 'companies', companyId, 'financialYears', financialYearId));
    fy = snap.exists() ? snap.data() : null;
    _numCacheSet(key, fy);
  }
  return fy;
};

// Speed (2026-09-30): the number is prepared in two parts.
//  1) prepareDocNumber — everything that does NOT need the lock: settings, year,
//     branch code, counter check. Its reads now run AT THE SAME TIME (they were
//     one after another — measured up to 7.6 s on the first save of a session).
//  2) The counter itself is increased inside a Firestore transaction. New
//     documents do this in the SAME transaction that saves the document (see
//     createTransactionImpl) — one server round trip fewer, and the number is
//     still strictly sequential with no gaps.
export const prepareDocNumber = async (companyId, tranTypeCode, docDate, branchId = null, financialYearId = null, opts = {}) => {
  const tranType = getTranType(tranTypeCode);
  if (!tranType) throw new Error(`Invalid transaction type: ${tranTypeCode}`);

  let customPrefix = tranType.prefix;
  let padding = 5;
  let startNumber = 1;
  const docKey = TRAN_CODE_TO_DOC_KEY[tranTypeCode];
  const calYear = new Date(docDate).getFullYear();
  const fyKey = financialYearId || `CY${calYear}`;
  const counterKey = `${tranTypeCode}__${branchId || 'none'}__${fyKey}`;
  const counterRef = doc(db, 'companies', companyId, 'documentCounters', counterKey);
  const scopeSeenKey = `cnt|${companyId}|${counterKey}`;

  const sk = `numset|${companyId}`;
  const bk = `br|${companyId}|${branchId}`;
  const [settingsData, fyData, brData, preCounterExists] = await Promise.all([
    // Look up user-configured prefix/padding (cached 30 min)
    (async () => {
      if (!docKey) return null;
      let v = _numCacheGet(sk);
      if (v === undefined) {
        try {
          const settingsSnap = await getDoc(doc(db, 'companies', companyId, 'settings', 'documentNumbering'));
          v = settingsSnap.exists() ? settingsSnap.data() : null;
          _numCacheSet(sk, v);
        } catch (e) {
          console.warn('Could not load documentNumbering settings, using defaults:', e?.message);
          v = null;
        }
      }
      return v;
    })(),
    // Financial-year code (FY start year goes into the number)
    financialYearId ? _getFinancialYearCached(companyId, financialYearId).catch(() => null) : Promise.resolve(null),
    // Branch code segment (only when a branch is set)
    (async () => {
      if (!branchId) return null;
      let v = _numCacheGet(bk);
      if (v === undefined) {
        try {
          const bSnap = await getDoc(doc(db, 'companies', companyId, 'branches', branchId));
          v = bSnap.exists() ? bSnap.data() : null;
          _numCacheSet(bk, v);
        } catch { v = null; }
      }
      return v;
    })(),
    // Does this (type, branch, FY) counter exist yet? (once per session)
    (async () => {
      if (_numCacheGet(scopeSeenKey)) return true;
      try {
        const preCounter = await getDoc(counterRef);
        return preCounter.exists() && Number(preCounter.data().current) > 0;
      } catch { return false; }
    })(),
  ]);

  const entry = settingsData && docKey ? settingsData[docKey] : null;
  if (entry) {
    if (entry.prefix && String(entry.prefix).trim()) {
      const raw = String(entry.prefix).trim();
      customPrefix = raw.endsWith('-') ? raw : `${raw}-`;
    }
    if (Number(entry.padding)) padding = Math.min(Math.max(Number(entry.padding), 3), 8);
    if (Number(entry.startNumber)) startNumber = Number(entry.startNumber);
  }

  let fyCode = String(calYear);
  if (financialYearId && fyData) {
    fyCode = String(fyData.startDate ? new Date(fyData.startDate).getFullYear() : calYear);
  }

  let branchSeg = '';
  const code = brData ? String(brData.branchCode || '').trim() : '';
  if (code) branchSeg = `${code.toUpperCase()}-`;

  if (preCounterExists) _numCacheSet(scopeSeenKey, true); // exists → no need to check again this session
  // Warm-up never runs the (heavier) first-document scan — that stays at save time.
  if (!preCounterExists && opts.prewarm) return null;

  // Seed-scan once, only when this scope's counter doesn't exist yet — the first
  // doc of a (type, branch, FY) continues from the highest existing sequence.
  const scannedMax = preCounterExists
    ? 0
    : await scanMaxForScope(companyId, tranTypeCode, branchId, financialYearId, calYear);

  return {
    counterRef,
    scopeSeenKey,
    seedFloor: Math.max(scannedMax, startNumber - 1),
    // Format: PREFIX-[BRANCH-]FY-NNNNN  e.g. SI-LHR-2526-00001 / SI-2526-00001
    format: (current) => `${customPrefix}${branchSeg}${fyCode}-${current.toString().padStart(padding, '0')}`,
    counterData: (current) => ({
      prefix: customPrefix,
      current,
      branchId: branchId || null,
      financialYearId: financialYearId || null,
      updatedAt: new Date().toISOString()
    }),
  };
};

/** Take the next number inside an open Firestore transaction. */
export const takeDocNumberInTransaction = async (transaction, prep) => {
  const counterDoc = await transaction.get(prep.counterRef);
  const stored = counterDoc.exists() ? (Number(counterDoc.data().current) || 0) : 0;
  // Seed = largest of stored counter, scanned-max (first use), or startNumber-1.
  const current = Math.max(stored, prep.seedFloor) + 1;
  transaction.set(prep.counterRef, prep.counterData(current));
  return prep.format(current);
};

/**
 * Speed (2026-09-30): called when a document screen OPENS. Loads in the
 * background everything the first save would otherwise wait for — numbering
 * settings, year, branch, system accounts, stock-count lock, locations, and
 * opens the save connection. It never writes anything and never takes a
 * number. Runs at most once per 10 minutes per screen type.
 */
const _prewarmed = new Map();
export const prewarmSave = (companyId, { tranTypeCode, branchId = null, financialYearId = null } = {}) => {
  try {
    if (!companyId || !tranTypeCode || !financialYearId) return;
    const br = branchId && branchId !== 'all' ? branchId : null;
    const key = `${companyId}|${tranTypeCode}|${br || ''}|${financialYearId}`;
    const last = _prewarmed.get(key);
    if (last && Date.now() - last < 10 * 60 * 1000) return;
    _prewarmed.set(key, Date.now());
    const today = new Date().toISOString().split('T')[0];
    const quiet = (p) => Promise.resolve(p).catch(() => null);
    Promise.all([
      // Number set-up + open the save connection (read-only; takes no number).
      quiet(prepareDocNumber(companyId, tranTypeCode, today, br, financialYearId, { prewarm: true })
        .then(prep => (prep ? runTransaction(db, async (t) => { await t.get(prep.counterRef); }) : null))),
      quiet(_getFinancialYearCached(companyId, financialYearId)),
      quiet(getLocationLocks(companyId)),
      quiet(loadInventoryMovementConfig(companyId)),
      quiet(prewarmSystemAccounts(companyId)),
      quiet(getItemsCached(companyId)),
      quiet(br ? getBranchLocation(companyId, br) : null),
    ]).then(() => { try { console.info(`[save-timer] ready: ${tranTypeCode} screen prepared for a fast first save`); } catch (e) { /* ignore */ } });
  } catch (e) { /* warm-up must never affect the screen */ }
};

export const generateDocNumber = async (companyId, tranTypeCode, docDate, branchId = null, financialYearId = null) => {
  const prep = await prepareDocNumber(companyId, tranTypeCode, docDate, branchId, financialYearId);
  const num = await runTransaction(db, (t) => takeDocNumberInTransaction(t, prep));
  _numCacheSet(prep.scopeSeenKey, true); // counter now exists for this scope
  return num;
};

// (Doc-number rollback removed: numbers are never reused — see deleteTransaction.)

// Build the live counter doc key for a (type, branch, financial-year) scope.
const counterScopeKey = (tranTypeCode, branchId, financialYearId) =>
  `${tranTypeCode}__${branchId || 'none'}__${financialYearId || `CY${new Date().getFullYear()}`}`;

// Read the current counter value (= last issued sequence) for a scope. Used by
// Document Settings to show the true running number.
export const getDocCounterCurrent = async (companyId, tranTypeCode, branchId = null, financialYearId = null) => {
  try {
    const ref = doc(db, 'companies', companyId, 'documentCounters', counterScopeKey(tranTypeCode, branchId, financialYearId));
    const snap = await getDoc(ref);
    return snap.exists() ? (Number(snap.data().current) || 0) : 0;
  } catch { return 0; }
};

// Set the NEXT number for a scope (writes current = next-1). Guards against
// reusing a number already issued: next must exceed the highest existing
// sequence in that (type, branch, FY) scope.
export const setDocCounterNext = async (companyId, tranTypeCode, branchId, financialYearId, nextNumber) => {
  const next = Math.floor(Number(nextNumber));
  if (!Number.isFinite(next) || next < 1) throw new Error('Next number must be a positive whole number.');
  const fallbackYear = new Date().getFullYear();
  const scannedMax = await scanMaxForScope(companyId, tranTypeCode, branchId || null, financialYearId || null, fallbackYear);
  if (next <= scannedMax) {
    throw new Error(`Next number must be greater than ${scannedMax} — that number is already used for this type in the selected year/branch.`);
  }
  const ref = doc(db, 'companies', companyId, 'documentCounters', counterScopeKey(tranTypeCode, branchId || null, financialYearId || null));
  await setDoc(ref, {
    current: next - 1,
    branchId: branchId || null,
    financialYearId: financialYearId || null,
    updatedAt: new Date().toISOString()
  }, { merge: true });
  return next;
};

/**
 * Validate transaction date against financial year
 */
export const validateTransactionDate = async (companyId, financialYearId, docDate, options = {}) => {
  // Firebase cost: cache the FY doc (its dates never change mid-year) so it isn't
  // re-read on every save. Reuses the numbering cache above (30-min TTL).
  const fy = await _getFinancialYearCached(companyId, financialYearId);

  if (!fy) {
    throw new Error('Financial year not found');
  }
  const transactionDate = new Date(docDate);
  const startDate = new Date(fy.startDate);
  const endDate = new Date(fy.endDate);

  if (transactionDate < startDate || transactionDate > endDate) {
    throw new Error(
      `Transaction date ${docDate} is outside financial year ${fy.yearCode} (${fy.startDate} to ${fy.endDate})`
    );
  }

  // Closed years block NEW postings by default. Callers editing an EXISTING
  // document pass { allowClosed: true } so corrections to a closed period stay
  // possible (the edit is audit-logged by updateTransactionImpl instead of hard
  // blocked — mistakes need fixing).
  if (fy.isClosed && !options.allowClosed) {
    throw new Error(`Financial year ${fy.yearCode} is closed. No new transactions allowed. To correct an existing entry, edit it directly.`);
  }

  return true;
};

/**
 * Get party's linked account from COA
 */
export const getPartyAccount = async (companyId, partyType, partyId) => {
  if (!partyType || !partyId) return null;

  // Firebase cost: resolve the party's ledger account from the device-cached
  // accounts (0 reads). Falls back to a live query only when the cache misses
  // (e.g. a brand-new customer whose account hasn't synced yet).
  try {
    const all = await getAccountsCached(companyId);
    const hit = all.find(a => a.linkedEntityType === partyType && a.linkedEntityId === partyId);
    if (hit) return hit;
  } catch (e) { /* fall through to live query */ }

  const accountsRef = collection(db, 'companies', companyId, 'accounts');
  const q = query(
    accountsRef,
    where('linkedEntityType', '==', partyType),
    where('linkedEntityId', '==', partyId)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const accountDoc = snapshot.docs[0];
  return {
    id: accountDoc.id,
    ...accountDoc.data()
  };
};

/**
 * Create a new transaction
 */
// ─── ACC-03 (2026-09-30): all-or-nothing saving ─────────────────────────────
// A document used to be saved in separate steps (header, then ledger rows, then
// stock rows). A lost connection or an error in between left a posted document
// with no accounting or no stock. The collector gathers EVERY write of one save
// and commits them together in a single Firestore batch — all or nothing, the
// same way the POS fast path already works. The header is always the LAST op,
// so in the rare case a document needs more than one batch (> 450 writes, e.g.
// a very large payroll) the document still only appears once all its rows exist.
// Side effects (item cost refresh, stock caches) run only after the commit.
const BATCH_LIMIT = 450;
// Replace the number placeholder in plain values (strings inside plain objects /
// arrays). Firestore special values (arrayUnion, serverTimestamp…) are untouched.
const _swapToken = (v, token, num) => {
  if (typeof v === 'string') return v.includes(token) ? v.split(token).join(num) : v;
  if (Array.isArray(v)) return v.map(x => _swapToken(x, token, num));
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    const o = {};
    Object.keys(v).forEach(k => { o[k] = _swapToken(v[k], token, num); });
    return o;
  }
  return v;
};
export const createWriteCollector = (timer = null) => {
  const ops = [];
  const after = [];
  return {
    set: (ref, data) => { ops.push({ type: 'set', ref, data }); },
    update: (ref, data) => { ops.push({ type: 'update', ref, data }); },
    delete: (ref) => { ops.push({ type: 'delete', ref }); },
    afterCommit: (fn) => { after.push(fn); },
    size: () => ops.length,
    commit: async () => {
      for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
        const b = writeBatch(db);
        ops.slice(i, i + BATCH_LIMIT).forEach(o => {
          if (o.type === 'set') b.set(o.ref, o.data);
          else if (o.type === 'update') b.update(o.ref, o.data);
          else b.delete(o.ref);
        });
        // eslint-disable-next-line no-await-in-loop
        await b.commit();
      }
      if (timer) timer.step('write to server');
      for (const fn of after) {
        // eslint-disable-next-line no-await-in-loop
        try { await fn(); } catch (e) { console.warn('[save] post-save step failed (data is saved):', e); }
      }
      if (timer && after.length) timer.step('after-save steps');
    },
    /**
     * Speed (2026-09-30): take the document number AND save everything in ONE
     * Firestore transaction (was: a transaction for the number, then a separate
     * save). Rows were built with `token` in place of the number; it is swapped
     * for the real number here. Numbers stay strictly sequential (no gaps): if
     * two users save at the same moment, Firestore retries one of them.
     * Very large documents (> 440 writes) fall back to number-then-save.
     */
    commitWithNumber: async (prep, token, guardRef = null) => {
      const swap = (v, num) => _swapToken(v, token, num);
      // INV-15: a document saved under a pre-generated id must never be saved
      // twice (a retry that crossed the first save) — refuse if it exists.
      const alreadySaved = () => {
        const e = new Error('This document was already saved — open it from the list.');
        e.code = 'already-saved';
        return e;
      };
      let number;
      if (ops.length <= 440) {
        number = await runTransaction(db, async (t) => {
          if (guardRef) {
            const ex = await t.get(guardRef);
            if (ex.exists()) throw alreadySaved();
          }
          const num = await takeDocNumberInTransaction(t, prep);
          ops.forEach(o => {
            if (o.type === 'set') t.set(o.ref, swap(o.data, num));
            else if (o.type === 'update') t.update(o.ref, swap(o.data, num));
            else t.delete(o.ref);
          });
          return num;
        });
        if (timer) timer.step('number + write to server (one step)');
      } else {
        if (guardRef && (await getDoc(guardRef)).exists()) throw alreadySaved();
        number = await runTransaction(db, (t) => takeDocNumberInTransaction(t, prep));
        if (timer) timer.step('document number');
        for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
          const b = writeBatch(db);
          ops.slice(i, i + BATCH_LIMIT).forEach(o => {
            if (o.type === 'set') b.set(o.ref, swap(o.data, number));
            else if (o.type === 'update') b.update(o.ref, swap(o.data, number));
            else b.delete(o.ref);
          });
          // eslint-disable-next-line no-await-in-loop
          await b.commit();
        }
        if (timer) timer.step('write to server');
      }
      _numCacheSet(prep.scopeSeenKey, true);
      for (const fn of after) {
        // eslint-disable-next-line no-await-in-loop
        try { await fn(); } catch (e) { console.warn('[save] post-save step failed (data is saved):', e); }
      }
      if (timer && after.length) timer.step('after-save steps');
      return number;
    }
  };
};

const createTransactionImpl = async (companyId, transactionData, userId, timer = null, opts = {}) => {
  const {
    tranTypeCode,
    docDate,
    financialYearId,
    branchId,
    branchName,
    partyType,
    partyId,
    partyName,
    lines = [], // StockFlow lines
    subTransactionLines = [], // For manual entries (JV, OB)
    ...otherData
  } = transactionData;

  // Validate transaction type
  const tranType = getTranType(tranTypeCode);
  if (!tranType) {
    throw new Error(`Invalid transaction type: ${tranTypeCode}`);
  }

  // Guard: post permission. Without it, only Draft can be saved.
  assertCanPost(tranTypeCode, transactionData.status);

  // Guard: admin must select a specific branch, not "All Branches"
  if (branchId === 'all') {
    throw new Error('Please select a specific branch before creating a transaction.');
  }

  // Validate financial year
  if (!financialYearId) {
    throw new Error('Financial year is required');
  }

  // Run independent pre-save steps in PARALLEL
  const partyPromise = (partyType && partyId)
    ? getPartyAccount(companyId, partyType, partyId)
    : Promise.resolve(null);

  // Speed: the parent document (if linked) is read in the same parallel round
  // as the number, instead of one more wait afterwards.
  const parentReadPromise = otherData.parentId
    ? getDoc(doc(db, 'companies', companyId, 'transactions', otherData.parentId)).catch(() => null)
    : Promise.resolve(null);

  const [, numberPrep, partyAccount, enrichedLines, , parentSnapPre] = await Promise.all([
    timed(timer, 'date check', validateTransactionDate(companyId, financialYearId, docDate)),
    timed(timer, 'number setup', prepareDocNumber(companyId, tranTypeCode, docDate, branchId || null, financialYearId || null)),
    timed(timer, 'party account', partyPromise),
    timed(timer, 'item details', enrichTransactionLines(companyId, lines)),
    // Stock Audit freeze: block stock-OUT at a location that is being counted.
    // Runs BEFORE any write, so a blocked save leaves nothing behind. Fast no-op
    // (one cached read of a tiny doc) when no audit is running — which is every
    // company until the module is switched on.
    timed(timer, 'stock-count lock', assertLocationsUnlocked(companyId, tranTypeCode, transactionData)),
    timed(timer, 'linked document', parentReadPromise),
  ]);
  if (timer) timer.step('checks (parallel)');
  // The real number is taken in the same transaction that saves the document
  // (see commitWithNumber); until then everything carries this placeholder.
  const docNumber = `\u2063DOCNO${Math.random().toString(36).slice(2)}\u2063`;

  // From here on, use enrichedLines as the canonical line array.
  let linesEnriched = Array.isArray(enrichedLines) ? enrichedLines : lines;

  // Get party account if applicable
  let partyAccountId = null;
  let partyAccountCode = null;
  if (partyAccount) {
    partyAccountId = partyAccount.id;
    partyAccountCode = partyAccount.code;
  } else if (otherData.partyAccountId) {
    // Resolution by linkedEntityType/linkedEntityId found nothing — e.g. a Salary
    // Staff ledger account that was created without the entity tags. Fall back to
    // the account the form explicitly selected so the party (debit) leg still
    // posts. Without this, ADV/LON/FNF drop the staff leg and the books don't
    // balance ("Debits 0.00 ≠ Credits …"). Customer/vendor docs are unaffected
    // because their lookup succeeds and takes precedence above.
    partyAccountId = otherData.partyAccountId;
    partyAccountCode = otherData.partyAccountCode || null;
  }

  // Calculate totals from lines
  let subtotal = 0;
  let totalTax = 0;
  let totalDiscount = 0;

  if (lines.length > 0) {
    lines.forEach(line => {
      const lineGross = (line.quantity || 0) * (line.unitPrice || 0);
      const lineDiscount = line.discountAmount || 0;
      const lineNet = lineGross - lineDiscount;
      const lineTax = line.taxAmount || 0;

      subtotal += lineGross;
      totalDiscount += lineDiscount;
      totalTax += lineTax;
    });
  }

  // Use the totals the form actually saved when present, else fall back to the
  // line-derived ones (manual entries with no totals).
  // NOTE: `??` (not `||`) is essential. The form saves `subtotal` ALREADY net of
  // line discounts and saves the footer discount separately — which is 0 when
  // only a line discount was used. With `||`, that legitimate 0 was treated as
  // "missing" and replaced by `totalDiscount` (the sum of LINE discounts), so the
  // line discount got subtracted a SECOND time downstream → "Debits ≠ Credits"
  // by exactly the line-discount amount. `??` keeps a real 0 as 0.
  const finalSubtotal = otherData.subtotal ?? subtotal;
  const finalDiscount = otherData.discountAmount ?? totalDiscount;
  const finalTax = otherData.taxAmount ?? totalTax;

  // WHT (Withholding Tax) support for purchases
  const whtRate = otherData.whtRate || 0;
  const whtAmount = otherData.whtAmount ?? (whtRate > 0 ? Math.round(finalSubtotal * (whtRate / 100) * 100) / 100 : 0);

  const finalTotal = otherData.totalAmount ?? (finalSubtotal - finalDiscount + finalTax - whtAmount);

  // Create transaction document
  const transactionDoc = {
    tranTypeCode,
    docNumber,
    docDate,
    financialYearId,
    branchId: branchId || null,
    branchName: branchName || null,
    partyType: partyType || null,
    partyId: partyId || null,
    partyName: partyName || null,
    partyAccountId,
    partyAccountCode,
    subtotal: finalSubtotal,
    discountPercent: otherData.discountPercent || 0,
    discountAmount: finalDiscount,
    taxableAmount: finalSubtotal - finalDiscount,
    taxAmount: finalTax,
    // WHT (Withholding Tax) fields
    whtRate: whtRate,
    whtAmount: whtAmount,
    whtAccountId: otherData.whtAccountId || null,
    totalAmount: finalTotal,
    // Aliased fields for report compatibility
    grossAmount: finalSubtotal,
    totalDiscount: finalDiscount,
    totalGST: finalTax,
    totalWHT: whtAmount,
    netAmount: finalTotal,
    // Payment tracking
    paidAmount: otherData.paidAmount || 0,
    balanceAmount: otherData.balanceAmount ?? finalTotal,
    status: otherData.status || tranType.defaultStatus,
    parentId: otherData.parentId || null,
    childIds: [],
    employeeId: otherData.employeeId || null,
    employeeName: otherData.employeeName || null,
    jobId: otherData.jobId || null,
    jobName: otherData.jobName || null,
    locationId: otherData.locationId || null,
    locationName: otherData.locationName || null,
    notes: otherData.notes || '',
    internalNotes: otherData.internalNotes || '',
    createdAt: new Date().toISOString(),
    createdBy: userId,
    updatedAt: new Date().toISOString(),
    updatedBy: userId
  };

  // Add any additional fields
  const excludeFields = [
    'tranTypeCode', 'docDate', 'financialYearId', 'partyType', 'partyId',
    'partyName', 'lines', 'subTransactionLines', 'subtotal', 'discountPercent',
    'discountAmount', 'taxAmount', 'totalAmount', 'parentId', 'employeeId',
    'employeeName', 'jobId', 'jobName', 'locationId', 'locationName', 'notes',
    'internalNotes', 'whtRate', 'whtAmount', 'whtAccountId', 'paidAmount', 'balanceAmount'
  ];

  Object.keys(otherData).forEach(key => {
    if (!excludeFields.includes(key) && transactionDoc[key] === undefined) {
      transactionDoc[key] = otherData[key];
    }
  });

  // Include lines in the initial save (avoids a separate updateDoc call)
  if (linesEnriched.length > 0) {
    transactionDoc.lines = stripUndefined(linesEnriched);
  }

  // ACC-03: the id is created up-front and NOTHING is written until the header,
  // ledger rows, stock rows and parent link are all ready — then one commit.
  const transactionsRef = collection(db, 'companies', companyId, 'transactions');
  // INV-15: a screen may pass a pre-generated id (opts.docId) so that a retry
  // after a dropped connection saves the SAME document, never a second one.
  const docRef = opts.docId ? doc(transactionsRef, opts.docId) : doc(transactionsRef);
  const transactionId = docRef.id;
  const writer = createWriteCollector(timer);

  // Read the parent once (its tran-type decides stock posting); link it in the
  // same all-or-nothing save. (Already fetched in the parallel round above.)
  let parentData = null;
  if (transactionDoc.parentId) {
    const parentRef = doc(db, 'companies', companyId, 'transactions', transactionDoc.parentId);
    const parentSnap = (parentSnapPre && transactionDoc.parentId === otherData.parentId)
      ? parentSnapPre
      : await getDoc(parentRef);
    if (parentSnap && parentSnap.exists()) {
      parentData = parentSnap.data();
      writer.update(parentRef, { childIds: arrayUnion(transactionId), updatedAt: new Date().toISOString() });
    }
  }

  // Run StockFlow and SubTransaction generation in PARALLEL (they are independent)
  const parallelOps = [];

  // Resolve parent's tran-type so shouldPostStock() can distinguish "linked
  // to a DC (skip)" from "linked to a SO/QT (still post)". Reuses the document
  // updateParentChildIds already fetched — no second read.
  const parentTranTypeCode = parentData ? (parentData.tranTypeCode || null) : null;
  const movementConfig = await loadInventoryMovementConfig(companyId);
  const shouldCreateStockFlow = shouldPostStock(tranTypeCode, parentTranTypeCode, movementConfig) && !tranType.manualStockFlow && lines.length > 0;

  // INV-04 weighted average: sale-type stock-out lines are priced at the item's
  // current average (stock row and Cost of Goods carry the SAME cost) and every
  // item's running average is updated in this same save.
  let costing = null;
  let newStockRows = [];
  if (shouldCreateStockFlow) {
    costing = await prepareStockCosting(companyId, { tranTypeCode, lines: linesEnriched, financialYearId, postsStock: true });
    linesEnriched = costing.lines;
    if (transactionDoc.lines) transactionDoc.lines = stripUndefined(linesEnriched);
    if (timer) timer.step('item costs');
  }

  if (shouldCreateStockFlow) {
    parallelOps.push(generateStockFlow(companyId, {
      transactionId,
      tranTypeCode,
      docNumber,
      docDate,
      financialYearId,
      branchId: transactionDoc.branchId,
      branchName: transactionDoc.branchName,
      locationId: transactionDoc.locationId,
      locationName: transactionDoc.locationName,
      status: transactionDoc.status,
      lines: linesEnriched
    }, userId, { writer }).then((rows) => { newStockRows = rows || []; return rows; }));
  }

  if (affectsAccounting(tranTypeCode)) {
    const isManual = tranType.manualEntry || tranTypeCode === 'OB' || subTransactionLines.length > 0;

    parallelOps.push(generateSubTransactions(companyId, {
      transactionId,
      tranTypeCode,
      docNumber,
      docDate,
      financialYearId,
      branchId: transactionDoc.branchId,
      branchName: transactionDoc.branchName,
      status: transactionDoc.status,
      partyType,
      partyAccountId,
      partyAccountCode,
      partyName,
      totalAmount: finalTotal,
      taxAmount: finalTax,
      subtotal: finalSubtotal,
      discountAmount: finalDiscount,
      whtAmount: whtAmount,
      // 236G/236H add-vs-deduct direction - must reach generateSaleInvoiceEntries
      // (else posting always treats WHT as deducted and unbalanced when add-mode).
      whtAddToTotal: transactionDoc.whtAddToTotal === true,
      // Human note + cheque no — forwarded so the posting builder can write a
      // rich, human-readable Particulars onto the party ledger line (else the
      // curated object hides them and the ledger shows only boilerplate).
      notes: otherData.notes || '',
      narration: otherData.narration || '',
      chequeNo: otherData.chequeNo || '',
      whtAccountId: transactionDoc.whtAccountId,
      // Totals-level charges recovered from customer / paid on purchase — must be
      // forwarded so the accounting builder can post their Cr/Dr legs (else unbalanced).
      transportationCharges: otherData.transportationCharges || 0,
      otherCharges: otherData.otherCharges || 0,
      bankAccountId: otherData.bankAccountId || null,
      bankAccountCode: otherData.bankAccountCode || null,
      bankAccountName: otherData.bankAccountName || null,
      cashAccountId: otherData.cashAccountId || null,
      cashAccountCode: otherData.cashAccountCode || null,
      cashAccountName: otherData.cashAccountName || null,
      // Payment fields for POS and other transaction types
      posPayments: otherData.posPayments || null,
      paymentMethod: otherData.paymentMethod || null,
      paymentAmount: otherData.paymentAmount || 0,
      paymentAccountId: otherData.paymentAccountId || null,
      paymentAccountCode: otherData.paymentAccountCode || null,
      paymentAccountName: otherData.paymentAccountName || null,
      // Optional GL overrides — when present, the standard Inventory Control (Dr on PI)
      // or Sales – General (Cr on SI) is replaced with the picked account.
      debitAccountId: otherData.debitAccountId || null,
      debitAccountCode: otherData.debitAccountCode || null,
      debitAccountName: otherData.debitAccountName || null,
      creditAccountId: otherData.creditAccountId || null,
      creditAccountCode: otherData.creditAccountCode || null,
      creditAccountName: otherData.creditAccountName || null,
      // IPI (Import Purchase Invoice) fields — used by generateImportPurchaseInvoiceEntries
      landedCharges: otherData.landedCharges || null,
      inventoryValue: otherData.inventoryValue || 0,
      baseSubtotal: otherData.baseSubtotal || 0,
      // Payroll Run (PAY) fields — used by generatePayrollRunEntries
      payrollLines: otherData.payrollLines || null,
      payrollMonth: otherData.payrollMonth || null,
      // Final Settlement (FNF) fields — used by generateFinalSettlementEntries
      fnfEarnings: otherData.fnfEarnings || null,
      fnfDeductions: otherData.fnfDeductions || null,
      loanRecovery: otherData.loanRecovery || 0,
      advanceRecovery: otherData.advanceRecovery || 0,
      totalEarnings: otherData.totalEarnings || 0,
      netPayable: otherData.netPayable || 0,
      // Manual Production (PRC) — outsource vendor charge + wastage write-off legs.
      // These must be forwarded or generateProductionReceiptEntries can't post the
      // vendor credit / wastage expense (it only receives this curated object).
      outsourceCharge: otherData.outsourceCharge || 0,
      // INV-05: manual production overheads — credited to their expense accounts
      // so WIP clears only the material portion (they were being counted twice).
      overheads: Array.isArray(otherData.overheads) ? otherData.overheads : [],
      outsourceVendorAccountId: otherData.outsourceVendorAccountId || null,
      outsourceVendorAccountCode: otherData.outsourceVendorAccountCode || null,
      outsourceVendorAccountName: otherData.outsourceVendorAccountName || null,
      wastageValue: otherData.wastageValue || 0,
      lines: linesEnriched,
      manualLines: isManual ? subTransactionLines : null,
      _skipDelete: true // New transaction, nothing to delete
    }, userId, { writer }));

    console.log('[createTransaction] SubTrans payment data:', {
      paymentAmount: otherData.paymentAmount,
      paymentAccountId: otherData.paymentAccountId,
      partyAccountId,
      status: transactionDoc.status,
      tranTypeCode
    });
  }

  // Build ledger + stock rows (any error — unbalanced entries, missing account,
  // duplicate serial — throws HERE, before a single write: nothing is saved).
  if (parallelOps.length > 0) {
    await Promise.all(parallelOps);
  }
  if (timer) timer.step('build ledger + stock rows');

  // INV-04: item running averages, in the same save as the rows.
  if (costing) costing.finish(writer, newStockRows);

  // INV-15: rows the screen writes itself (manual stock / ledger rows, linked
  // documents) join this SAME all-or-nothing save. They may use `docNumber`
  // (the placeholder) — it is replaced by the real number at commit.
  if (typeof opts.extraWrites === 'function') {
    await opts.extraWrites(writer, { transactionId, docNumber });
  }

  // Header LAST, then one commit for everything (ACC-03) — number included.
  writer.set(docRef, stripUndefined(transactionDoc));
  const realDocNumber = await writer.commitWithNumber(numberPrep, docNumber, opts.docId ? docRef : null);

  return { id: transactionId, ..._swapToken(transactionDoc, docNumber, realDocNumber), docNumber: realDocNumber };
};

// A signature of the transaction being created — identical rapid Save clicks
// produce the SAME key, so we can collapse them into one write.
const _createKey = (companyId, d) => {
  try {
    const lines = (d.lines || [])
      .map(l => `${l.itemId || l.description || ''}:${l.quantity || 0}:${l.unitPrice || 0}`)
      .join('|');
    return [companyId, d.tranTypeCode, d.partyId || '', d.docDate || '', d.totalAmount || 0, d.paymentAmount || 0, lines].join('~');
  } catch {
    return null;
  }
};
const _inFlightCreates = new Map();

/**
 * Public createTransaction. Two protections layered here:
 *  1) DOUBLE-SUBMIT GUARD: if an identical create is already in flight (user
 *     clicked Save 3-4 times before the button disabled), return the SAME
 *     promise instead of writing a second/third duplicate transaction.
 *  2) GLOBAL BUSY OVERLAY: begin/end the app-wide loader so every form shows a
 *     professional "Saving…" spinner with no per-form code.
 */
export const createTransaction = (companyId, transactionData, userId, opts = {}) => {
  const key = _createKey(companyId, transactionData);
  if (key && _inFlightCreates.has(key)) return _inFlightCreates.get(key);
  beginBusy('Saving…');
  const timer = startSaveTimer(`Create ${transactionData?.tranTypeCode || ''}`);
  const p = createTransactionImpl(companyId, transactionData, userId, timer, opts)
    .then((r) => { timer.setLabel(`Create ${r?.docNumber || transactionData?.tranTypeCode || ''}`); timer.end(); return r; },
          (e) => { timer.end('failed'); throw e; })
    .finally(() => { if (key) _inFlightCreates.delete(key); endBusy(); });
  if (key) _inFlightCreates.set(key, p);
  return p;
};

/**
 * Update transaction status
 */
export const updateTransactionStatus = async (companyId, transactionId, newStatus, userId) => {
  const timer = startSaveTimer(`Status → ${newStatus}`);
  const transactionRef = doc(db, 'companies', companyId, 'transactions', transactionId);
  const transactionDoc = await getDoc(transactionRef);
  timer.step('read document');

  if (!transactionDoc.exists()) {
    timer.end('failed');
    throw new Error('Transaction not found');
  }

  const transaction = transactionDoc.data();
  const tranType = getTranType(transaction.tranTypeCode);
  timer.setLabel(`Status ${transaction.docNumber || transactionId} → ${newStatus}`);

  // Speed: already in this status (e.g. a document just created as Posted) —
  // nothing to change, skip the extra reads and the write.
  if (transaction.status === newStatus) {
    timer.end('no change');
    return { ...transaction };
  }

  // Guard: post permission. Status transitions out of Draft require post rights.
  assertCanPost(transaction.tranTypeCode, newStatus);

  // Validate status
  if (!tranType.statuses.includes(newStatus)) {
    timer.end('failed');
    throw new Error(`Invalid status '${newStatus}' for ${tranType.name}`);
  }

  const updateData = {
    status: newStatus,
    updatedAt: new Date().toISOString(),
    updatedBy: userId
  };

  // Add posted timestamp if changing to Posted
  if (newStatus === 'Posted' && transaction.status !== 'Posted') {
    updateData.postedAt = new Date().toISOString();
    updateData.postedBy = userId;
  }

  // ACC-03: header + ledger + stock status change in ONE commit (header last).
  // Speed: ledger and stock rows are looked up at the same time (was one after
  // the other).
  const [subTransDocs, stockFlowDocs] = await Promise.all([
    getDocs(query(collection(db, 'companies', companyId, 'subTransactions'), where('transactionId', '==', transactionId))),
    getDocs(query(collection(db, 'companies', companyId, 'stockFlow'), where('transactionId', '==', transactionId))),
  ]);
  timer.step('read ledger + stock rows');

  // Ledger lines use the normalized posting status (e.g. ADV "Issued" → "Posted")
  // so they remain visible in the journal/ledger; the transaction doc keeps the
  // business status set above. Cancelled/Draft pass through unchanged (stay hidden).
  const ledgerStatus = normalizeLedgerStatus(newStatus);
  const batch = createWriteCollector(timer);
  subTransDocs.forEach(d => {
    batch.update(d.ref, { status: ledgerStatus });
  });
  stockFlowDocs.forEach(d => {
    batch.update(d.ref, { status: newStatus });
  });
  // INV-04: rows that start or stop counting (e.g. Draft → Posted, Posted →
  // Cancelled) move the items' running averages in this same save.
  if (stockFlowDocs.size > 0) {
    const oldRows = stockFlowDocs.docs.map(d => ({ id: d.id, ...d.data() }));
    await costRowsChange(companyId, batch, {
      added: oldRows.map(r => ({ ...r, status: newStatus })),
      removed: oldRows,
      financialYearId: transaction.financialYearId,
    });
  }

  batch.update(transactionRef, updateData);
  await batch.commit();
  timer.end();

  // AUDIT (2026-11-04): every cancel is recorded — who, when, which document —
  // for the User Activity Log. Fire-and-forget: logging never blocks the cancel.
  if (newStatus === 'Cancelled') {
    writeAuditLog(
      companyId,
      auth.currentUser?.uid || userId || null,
      'cancel',
      transaction.tranTypeCode || 'transaction',
      transactionId,
      {
        docNumber: transaction.docNumber || '',
        docDate: transaction.docDate || '',
        partyName: transaction.partyName || '',
        amount: Number(transaction.totalAmount) || 0,
        branchId: transaction.branchId || null,
        branchName: transaction.branchName || '',
        status: transaction.status || '',
      }
    ).catch(() => { /* never block a cancel on logging */ });
  }

  return { ...transaction, ...updateData };
};

/**
 * Manual closing of a Sale Order / Purchase Order (2026-10-09).
 * The order stays exactly as entered (e.g. ordered 100, delivered 98) — it only
 * stops waiting for the rest. Status 'Closed' takes it off every "Link to SO /
 * PO" list (those load only Approved / Partially … orders) and the tracking
 * reports show the remainder as closed, not pending. Deliveries / receipts
 * that are edited later still update its lines; the status stays Closed (the
 * progress recount keeps any status that is not an open one). Reopen puts it
 * back to the status its deliveries / receipts give.
 */
const ORDER_CLOSE = {
  SO: { doneField: 'deliveredQty', open: ['Approved', 'Partially Delivered'], partial: 'Partially Delivered' },
  PO: { doneField: 'receivedQty', open: ['Approved', 'Partially Received'], partial: 'Partially Received' },
};

/** Lines still open on an order: [{ label, unit, ordered, done, left }]. */
export const orderPendingLines = (order) => {
  const cfg = ORDER_CLOSE[order?.tranTypeCode];
  if (!cfg) return [];
  return (order.lines || []).map((l, i) => {
    const ordered = Number(l.quantity) || 0;
    const done = Number(l[cfg.doneField]) || 0;
    return {
      label: l.description || l.itemName || `Line ${i + 1}`,
      unit: l.unitOfMeasure || '',
      ordered,
      done,
      left: Math.max(0, Math.round((ordered - done) * 1e6) / 1e6),
    };
  }).filter(x => x.left > 0);
};

export const closeOrder = async (companyId, orderId, { userId = null, userName = null, reason = '' } = {}) => {
  const ref = doc(db, 'companies', companyId, 'transactions', orderId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Order not found');
  const order = snap.data();
  const cfg = ORDER_CLOSE[order.tranTypeCode];
  if (!cfg) throw new Error('Only sale orders and purchase orders can be closed.');
  if (!cfg.open.includes(order.status)) throw new Error(`This order is ${order.status} — only an open order can be closed.`);
  const now = new Date().toISOString();
  const upd = {
    status: 'Closed',
    closedAt: now,
    closedBy: userId,
    closedByName: userName,
    closeReason: String(reason || '').trim().slice(0, 300),
    statusBeforeClose: order.status,
    updatedAt: now,
    ...(userId ? { updatedBy: userId } : {}),
  };
  await updateDoc(ref, upd);
  return { ...order, ...upd };
};

export const reopenOrder = async (companyId, orderId, { userId = null } = {}) => {
  const ref = doc(db, 'companies', companyId, 'transactions', orderId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Order not found');
  const order = snap.data();
  const cfg = ORDER_CLOSE[order.tranTypeCode];
  if (!cfg) throw new Error('Only sale orders and purchase orders can be reopened.');
  if (order.status !== 'Closed') throw new Error(`This order is ${order.status} — it is not closed.`);
  const lines = Array.isArray(order.lines) ? order.lines : [];
  const done = (l) => Number(l[cfg.doneField]) || 0;
  const all = lines.length > 0 && lines.every(l => done(l) >= (Number(l.quantity) || 0));
  const some = lines.some(l => done(l) > 0);
  const now = new Date().toISOString();
  const upd = {
    status: all ? 'Completed' : some ? cfg.partial : 'Approved',
    closedAt: null,
    closedBy: null,
    closedByName: null,
    closeReason: null,
    statusBeforeClose: null,
    reopenedAt: now,
    reopenedBy: userId,
    updatedAt: now,
    ...(userId ? { updatedBy: userId } : {}),
  };
  await updateDoc(ref, upd);
  return { ...order, ...upd };
};

/**
 * Update a transaction
 */
const updateTransactionImpl = async (companyId, transactionId, updateData, userId, timer = null, opts = {}) => {
  const transactionRef = doc(db, 'companies', companyId, 'transactions', transactionId);
  const transactionDoc = await getDoc(transactionRef);
  if (timer) { timer.setLabel(`Edit ${transactionDoc.exists() ? (transactionDoc.data().docNumber || '') : transactionId}`); timer.step('read document'); }

  if (!transactionDoc.exists()) {
    throw new Error('Transaction not found');
  }

  const existingTransaction = { id: transactionId, ...transactionDoc.data() };
  const tranTypeCode = existingTransaction.tranTypeCode;
  const tranType = getTranType(tranTypeCode);

  // ACC-16: documents registered with FBR are locked — their content must stay
  // exactly what FBR holds. Payment/status bookkeeping still updates elsewhere.
  if ((existingTransaction.fbrInvoiceNumber || existingTransaction.diIrn) &&
      ['lines', 'totalAmount', 'docDate', 'partyId', 'subtotal', 'taxAmount'].some((k) => k in updateData)) {
    throw new Error(`This document is registered with FBR (${existingTransaction.fbrInvoiceNumber ? 'FBR #' + existingTransaction.fbrInvoiceNumber : 'IRN ' + existingTransaction.diIrn}) and cannot be edited. Issue a return instead.`);
  }

  // ACC-05: editing a live invoice keeps it live. Pages send 'Posted' when a
  // posted/part-paid/paid invoice is re-saved; derive the real payment status
  // from the amounts so a Paid or Partial invoice never falls back to 'Posted'.
  const OPEN_LIVE = ['Posted', 'Partial', 'Paid'];
  const liveEdit = OPEN_LIVE.includes(existingTransaction.status) && OPEN_LIVE.includes(updateData.status);
  if (liveEdit && ['SI', 'NSI', 'PI', 'NPI'].includes(tranTypeCode)) {
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const total = r2(updateData.totalAmount ?? existingTransaction.totalAmount);
    const paid = r2(updateData.paidAmount ?? existingTransaction.paidAmount);
    const returned = r2(existingTransaction.returnedAmount);
    const balance = Math.max(0, r2(total - paid - returned));
    updateData = {
      ...updateData,
      balanceAmount: balance,
      status: (paid <= 0 && returned <= 0) ? 'Posted' : (balance <= 0.01 ? 'Paid' : 'Partial'),
    };
  }

  // Guard: post permission. Block status transitions that require post rights.
  // Posted ⇄ Partial ⇄ Paid on an edit is payment-driven, not a posting action.
  if (updateData.status && updateData.status !== existingTransaction.status && !liveEdit) {
    assertCanPost(tranTypeCode, updateData.status);
  }

  // Guard: prevent saving with 'all' as branchId
  if (updateData.branchId === 'all') {
    throw new Error('Please select a specific branch before saving.');
  }

  // Closed-year edits are ALLOWED (corrections happen) but never silent — leave
  // an audit trail. Placed before the date check so it also covers same-date
  // edits (amounts/lines only) that would otherwise skip validation entirely.
  try {
    const editFyId = existingTransaction.financialYearId;
    if (editFyId) {
      // Cached: this fires on EVERY edit-save and re-read the same FY doc the
      // date validation and numbering had already fetched.
      const editFy = await _getFinancialYearCached(companyId, editFyId);
      if (editFy && editFy.isClosed) {
        writeAuditLog(
          companyId,
          auth.currentUser?.uid || null,
          'closed-year-edit',
          existingTransaction.tranTypeCode || 'transaction',
          transactionId,
          {
            docNumber: existingTransaction.docNumber || '',
            docDate: existingTransaction.docDate || '',
            yearCode: editFy.yearCode || '',
            partyName: existingTransaction.partyName || '',
            amount: Number(updateData.totalAmount) || Number(existingTransaction.totalAmount) || 0,
          }
        ).catch(() => { /* never block an edit on logging */ });
      }
    }
  } catch (e) { /* audit is best-effort; never block an edit */ }

  // Validate date if changed — against the transaction's OWN financial year, not
  // the currently active one, so an edit can never push a document's date into a
  // different year than the one it already belongs to. allowClosed lets a closed
  // year's entry be corrected (audit-logged above); NEW postings still block.
  if (updateData.docDate && updateData.docDate !== existingTransaction.docDate) {
    await validateTransactionDate(
      companyId,
      existingTransaction.financialYearId || updateData.financialYearId,
      updateData.docDate,
      { allowClosed: true }
    );
  }

  // Prepare update
  const { lines: rawLines, subTransactionLines, ...otherUpdates } = updateData;

  // Enrich lines with multi-unit fields before any downstream use.
  const lines = (rawLines && rawLines.length > 0)
    ? await enrichTransactionLines(companyId, rawLines)
    : rawLines;

  const finalUpdate = {
    ...otherUpdates,
    updatedAt: new Date().toISOString(),
    updatedBy: userId
  };

  // A transaction's financial year is fixed by its original period — editing must
  // NEVER move it. The document forms pass financialYearId = the currently ACTIVE
  // year, so without this an edit made while a different year is active would
  // silently re-file the document (e.g. last year's invoice jumps into this year).
  // Keep the original; only fall back to the incoming value for legacy rows that
  // were never tagged. Regenerated stockFlow / subTransactions below inherit this.
  if (existingTransaction.financialYearId) {
    finalUpdate.financialYearId = existingTransaction.financialYearId;
  }

  // Recalculate totals from lines ONLY if caller didn't provide them
  // (Caller-provided values may include WHT, header discounts, etc.)
  if (lines && lines.length > 0) {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    lines.forEach(line => {
      const lineGross = (line.quantity || 0) * (line.unitPrice || 0);
      const lineDiscount = line.discountAmount || 0;
      const lineTax = line.taxAmount || 0;

      subtotal += lineGross;
      totalDiscount += lineDiscount;
      totalTax += lineTax;
    });

    // Only use calculated values if caller didn't provide them
    if (otherUpdates.subtotal === undefined) finalUpdate.subtotal = subtotal;
    if (otherUpdates.discountAmount === undefined) finalUpdate.discountAmount = totalDiscount;
    if (otherUpdates.taxAmount === undefined) finalUpdate.taxAmount = totalTax;
    if (otherUpdates.taxableAmount === undefined) finalUpdate.taxableAmount = subtotal - totalDiscount;
    if (otherUpdates.totalAmount === undefined) finalUpdate.totalAmount = subtotal - totalDiscount + totalTax;
  }

  // Sync aliased fields for report compatibility
  const updSubtotal = finalUpdate.subtotal ?? existingTransaction.subtotal ?? 0;
  const updDiscount = finalUpdate.discountAmount ?? existingTransaction.discountAmount ?? 0;
  const updTax = finalUpdate.taxAmount ?? existingTransaction.taxAmount ?? 0;
  const updWht = finalUpdate.whtAmount ?? otherUpdates.whtAmount ?? existingTransaction.whtAmount ?? 0;
  const updTotal = finalUpdate.totalAmount ?? existingTransaction.totalAmount ?? 0;
  finalUpdate.grossAmount = updSubtotal;
  finalUpdate.totalDiscount = updDiscount;
  finalUpdate.totalGST = updTax;
  finalUpdate.totalWHT = updWht;
  finalUpdate.netAmount = updTotal;

  // Include lines in the update (avoids a separate updateDoc call)
  if (lines && lines.length > 0) {
    finalUpdate.lines = stripUndefined(lines);
  }

  // Resolve party account if missing or party changed
  const resolvedPartyType = finalUpdate.partyType || existingTransaction.partyType;
  const resolvedPartyId = finalUpdate.partyId || existingTransaction.partyId;
  if (resolvedPartyType && resolvedPartyId &&
      (finalUpdate.partyId !== existingTransaction.partyId || !existingTransaction.partyAccountId)) {
    const partyAccount = await getPartyAccount(companyId, resolvedPartyType, resolvedPartyId);
    if (partyAccount) {
      finalUpdate.partyAccountId = partyAccount.id;
      finalUpdate.partyAccountCode = partyAccount.code;
    }
  }

  // Stock Audit freeze — same guard on edits, before anything is written.
  await assertLocationsUnlocked(companyId, tranTypeCode, {
    status: finalUpdate.status || existingTransaction.status,
    locationId: finalUpdate.locationId ?? existingTransaction.locationId,
    branchId: existingTransaction.branchId,
    lines: lines || existingTransaction.lines || [],
  });

  // ACC-03: header update, old-row removal and new rows are committed together
  // below (see createWriteCollector). Nothing is written until all are built.
  const writer = createWriteCollector(timer);
  if (timer) timer.step('checks');

  // Determine what needs regeneration. Resolve parent's tran-type so the
  // SI/NSI skip rule fires only for DC-linked invoices, not for SO/QT-linked.
  const effectiveParentId = existingTransaction.parentId || finalUpdate.parentId;
  let parentTranTypeCodeUpd = null;
  if (effectiveParentId) {
    try {
      const parentSnap = await getDoc(doc(db, 'companies', companyId, 'transactions', effectiveParentId));
      if (parentSnap.exists()) parentTranTypeCodeUpd = parentSnap.data().tranTypeCode || null;
    } catch (e) { /* fall back to null */ }
  }
  const movementConfigForUpdate = await loadInventoryMovementConfig(companyId);
  const shouldUpdateStockFlow = lines && shouldPostStock(tranTypeCode, parentTranTypeCodeUpd, movementConfigForUpdate) && !tranType.manualStockFlow;
  // Salary docs (Advance/Loan/Payroll/Final Settlement) build their accounting
  // purely from header/totals fields — they carry no stock `lines` and no manual
  // `subTransactionLines`. Without listing them here, re-saving one skips ledger
  // regeneration entirely, so its entries keep their old status and never refresh
  // into the journal. The regen block below forwards every field they need.
  const HEADER_AUTO_ACCOUNTING = new Set(['ADV', 'LON', 'LRC', 'PAY', 'FNF']);
  const shouldUpdateAccounting = affectsAccounting(tranTypeCode) &&
    (lines || subTransactionLines || HEADER_AUTO_ACCOUNTING.has(tranTypeCode));

  // Step 1: find the existing rows (they are replaced in the same commit)
  const [oldStockRows, oldSubIds] = await Promise.all([
    shouldUpdateStockFlow ? listStockFlowRows(companyId, transactionId) : Promise.resolve([]),
    shouldUpdateAccounting ? listSubTransactionIds(companyId, transactionId) : Promise.resolve([]),
  ]);

  // INV-04 weighted average: price the new stock-out lines at the item's
  // average WITHOUT this document's old rows, and update the running averages
  // (old rows out, new rows in) in this same save.
  let costing = null;
  let costedLines = lines;
  let newStockRows = [];
  if (shouldUpdateStockFlow) {
    costing = await prepareStockCosting(companyId, {
      tranTypeCode,
      lines,
      financialYearId: finalUpdate.financialYearId || existingTransaction.financialYearId,
      removedRows: oldStockRows,
      postsStock: true,
    });
    costedLines = costing.lines;
    if (finalUpdate.lines) finalUpdate.lines = stripUndefined(costedLines);
  }

  // Step 2: Regenerate in PARALLEL
  const regenOps = [];
  if (shouldUpdateStockFlow) {
    regenOps.push(generateStockFlow(companyId, {
      transactionId,
      tranTypeCode,
      docNumber: existingTransaction.docNumber,
      docDate: finalUpdate.docDate || existingTransaction.docDate,
      financialYearId: finalUpdate.financialYearId || existingTransaction.financialYearId,
      branchId: existingTransaction.branchId || null,
      branchName: existingTransaction.branchName || null,
      locationId: finalUpdate.locationId || existingTransaction.locationId,
      locationName: finalUpdate.locationName || existingTransaction.locationName,
      status: finalUpdate.status || existingTransaction.status,
      lines: costedLines
    }, userId, { writer }).then((rows) => { newStockRows = rows || []; return rows; }));
  }

  if (shouldUpdateAccounting) {
    const isManual = tranType.manualEntry || tranTypeCode === 'OB' || (subTransactionLines && subTransactionLines.length > 0);

    regenOps.push(generateSubTransactions(companyId, {
      transactionId,
      tranTypeCode,
      docNumber: existingTransaction.docNumber,
      docDate: finalUpdate.docDate || existingTransaction.docDate,
      financialYearId: finalUpdate.financialYearId || existingTransaction.financialYearId,
      branchId: existingTransaction.branchId || null,
      branchName: existingTransaction.branchName || null,
      status: finalUpdate.status || existingTransaction.status,
      partyType: finalUpdate.partyType || existingTransaction.partyType,
      partyAccountId: finalUpdate.partyAccountId || existingTransaction.partyAccountId,
      partyAccountCode: finalUpdate.partyAccountCode || existingTransaction.partyAccountCode,
      partyName: finalUpdate.partyName || existingTransaction.partyName,
      // `??` (not `||`) so a legitimate 0 (e.g. no footer discount, only a line
      // discount) is kept as 0 instead of falling back to the old value — same
      // double-count bug the create path had.
      totalAmount: finalUpdate.totalAmount ?? existingTransaction.totalAmount,
      taxAmount: finalUpdate.taxAmount ?? existingTransaction.taxAmount,
      subtotal: finalUpdate.subtotal ?? existingTransaction.subtotal,
      discountAmount: finalUpdate.discountAmount ?? existingTransaction.discountAmount,
      whtAmount: updWht,
      // 236G/236H add-vs-deduct - forward on edit too (?? so false is kept).
      whtAddToTotal: (otherUpdates.whtAddToTotal ?? existingTransaction.whtAddToTotal) === true,
      // Human note + cheque no — forwarded on edit too so the re-posted party
      // ledger line keeps its rich Particulars.
      notes: otherUpdates.notes ?? existingTransaction.notes ?? '',
      narration: otherUpdates.narration ?? existingTransaction.narration ?? '',
      chequeNo: otherUpdates.chequeNo ?? existingTransaction.chequeNo ?? '',
      whtAccountId: otherUpdates.whtAccountId || existingTransaction.whtAccountId || null,
      // Totals-level charges — forward on edit too, else regenerated entries unbalance.
      transportationCharges: otherUpdates.transportationCharges ?? existingTransaction.transportationCharges ?? 0,
      otherCharges: otherUpdates.otherCharges ?? existingTransaction.otherCharges ?? 0,
      bankAccountId: otherUpdates.bankAccountId || existingTransaction.bankAccountId || null,
      bankAccountCode: otherUpdates.bankAccountCode || existingTransaction.bankAccountCode || null,
      bankAccountName: otherUpdates.bankAccountName || existingTransaction.bankAccountName || null,
      cashAccountId: otherUpdates.cashAccountId || existingTransaction.cashAccountId || null,
      cashAccountCode: otherUpdates.cashAccountCode || existingTransaction.cashAccountCode || null,
      cashAccountName: otherUpdates.cashAccountName || existingTransaction.cashAccountName || null,
      // Payment fields for POS and other transaction types
      posPayments: otherUpdates.posPayments || existingTransaction.posPayments || null,
      paymentMethod: otherUpdates.paymentMethod || existingTransaction.paymentMethod || null,
      paymentAmount: otherUpdates.paymentAmount || existingTransaction.paymentAmount || 0,
      paymentAccountId: otherUpdates.paymentAccountId || existingTransaction.paymentAccountId || null,
      paymentAccountCode: otherUpdates.paymentAccountCode || existingTransaction.paymentAccountCode || null,
      paymentAccountName: otherUpdates.paymentAccountName || existingTransaction.paymentAccountName || null,
      // GL overrides — forwarded for SubTransaction regeneration on edit
      debitAccountId: otherUpdates.debitAccountId || existingTransaction.debitAccountId || null,
      debitAccountCode: otherUpdates.debitAccountCode || existingTransaction.debitAccountCode || null,
      debitAccountName: otherUpdates.debitAccountName || existingTransaction.debitAccountName || null,
      creditAccountId: otherUpdates.creditAccountId || existingTransaction.creditAccountId || null,
      creditAccountCode: otherUpdates.creditAccountCode || existingTransaction.creditAccountCode || null,
      creditAccountName: otherUpdates.creditAccountName || existingTransaction.creditAccountName || null,
      // IPI fields — forwarded for SubTransaction regeneration on edit
      landedCharges: otherUpdates.landedCharges || existingTransaction.landedCharges || null,
      inventoryValue: otherUpdates.inventoryValue ?? existingTransaction.inventoryValue ?? 0,
      baseSubtotal: otherUpdates.baseSubtotal ?? existingTransaction.baseSubtotal ?? 0,
      // PAY (Payroll) fields — forwarded for SubTransaction regeneration on edit
      payrollLines: otherUpdates.payrollLines || existingTransaction.payrollLines || null,
      payrollMonth: otherUpdates.payrollMonth || existingTransaction.payrollMonth || null,
      // FNF fields — forwarded for SubTransaction regeneration on edit
      fnfEarnings: otherUpdates.fnfEarnings || existingTransaction.fnfEarnings || null,
      fnfDeductions: otherUpdates.fnfDeductions || existingTransaction.fnfDeductions || null,
      loanRecovery: otherUpdates.loanRecovery ?? existingTransaction.loanRecovery ?? 0,
      advanceRecovery: otherUpdates.advanceRecovery ?? existingTransaction.advanceRecovery ?? 0,
      totalEarnings: otherUpdates.totalEarnings ?? existingTransaction.totalEarnings ?? 0,
      netPayable: otherUpdates.netPayable ?? existingTransaction.netPayable ?? 0,
      // Manual Production (PRC) — outsource vendor charge + wastage, forwarded on edit
      // too so a re-post regenerates the vendor credit / wastage write-off legs.
      outsourceCharge: otherUpdates.outsourceCharge ?? existingTransaction.outsourceCharge ?? 0,
      // INV-05: forward overheads on edit too.
      overheads: otherUpdates.overheads ?? existingTransaction.overheads ?? [],
      outsourceVendorAccountId: otherUpdates.outsourceVendorAccountId ?? existingTransaction.outsourceVendorAccountId ?? null,
      outsourceVendorAccountCode: otherUpdates.outsourceVendorAccountCode ?? existingTransaction.outsourceVendorAccountCode ?? null,
      outsourceVendorAccountName: otherUpdates.outsourceVendorAccountName ?? existingTransaction.outsourceVendorAccountName ?? null,
      wastageValue: otherUpdates.wastageValue ?? existingTransaction.wastageValue ?? 0,
      lines: costedLines || [],
      manualLines: isManual ? subTransactionLines : null
    }, userId, { writer }));
  }

  // Build the new rows (throws before any write on error)
  if (timer) timer.step('read old rows + linked doc');
  const regenResults = regenOps.length > 0 ? await Promise.all(regenOps) : [];
  if (timer) timer.step('build ledger + stock rows');
  const newIds = new Set();
  regenResults.forEach(rows => (rows || []).forEach(r => r && r.id && newIds.add(r.id)));

  // Old rows that are not overwritten by a new row with the same id are removed
  // in the SAME commit — the document is never left without its entries.
  oldStockRows.forEach(r => {
    if (!newIds.has(r.id)) {
      writer.delete(doc(db, 'companies', companyId, 'stockFlow', r.id));
    }
  });
  oldSubIds.forEach(id => {
    if (!newIds.has(id)) writer.delete(doc(db, 'companies', companyId, 'subTransactions', id));
  });
  if (oldStockRows.length > 0) {
    // Undo the replaced rows in the cached stock badges (new rows are folded in
    // by generateStockFlow's own post-save step).
    writer.afterCommit(() => {
      applyEntriesToStockCache(companyId, oldStockRows, -1);
      invalidateItemStock(companyId);
    });
  }

  // INV-04: item running averages (old rows out, new rows in), same save.
  if (costing) costing.finish(writer, newStockRows);

  // INV-11 / INV-15: rows the screen writes itself (manual stock / ledger rows,
  // linked documents) join this SAME all-or-nothing save.
  if (typeof opts.extraWrites === 'function') {
    await opts.extraWrites(writer, { transactionId, docNumber: existingTransaction.docNumber });
  }

  // Header LAST, then commit everything together.
  writer.update(transactionRef, stripUndefined(finalUpdate));
  await writer.commit();

  return {
    ...existingTransaction,
    ...finalUpdate
  };
};

/**
 * Public updateTransaction — wraps the impl with the global busy overlay so an
 * edit-save shows the same professional "Saving…" loader. (Re-saving an edit is
 * idempotent — it overwrites the same doc — so no de-dup key is needed here.)
 */
export const updateTransaction = (companyId, transactionId, updateData, userId, opts = {}) => {
  beginBusy('Saving…');
  const timer = startSaveTimer('Edit');
  return updateTransactionImpl(companyId, transactionId, updateData, userId, timer, opts)
    .then((r) => { timer.end(); return r; }, (e) => { timer.end('failed'); throw e; })
    .finally(endBusy);
};

/**
 * Delete a transaction (hard delete)
 */
const deleteTransactionImpl = async (companyId, transactionId) => {
  const transactionRef = doc(db, 'companies', companyId, 'transactions', transactionId);
  const transactionDoc = await getDoc(transactionRef);

  if (!transactionDoc.exists()) {
    throw new Error('Transaction not found');
  }

  const transaction = transactionDoc.data();

  // Check if transaction has children
  if (transaction.childIds && transaction.childIds.length > 0) {
    throw new Error('Cannot delete transaction with linked child documents');
  }

  // Guard ADV/LON: prevent deletion after any recovery has been posted against this doc
  if (['ADV', 'LON'].includes(transaction.tranTypeCode) && (Number(transaction.recoveredAmount) || 0) > 0) {
    throw new Error(
      `Cannot delete ${transaction.tranTypeCode} ${transaction.docNumber}: ` +
      `${Number(transaction.recoveredAmount).toFixed(2)} already recovered via payroll/F&F. ` +
      `Cancel the related payroll/F&F first, then retry.`
    );
  }

  // Guard invoices cleared by a SEPARATE receipt/payment (SI/PI/NSI etc.).
  // Inline payment entered on the invoice itself is stored as `paymentAmount` and
  // its cash/bank leg lives in THIS document's own sub-transactions — deleting the
  // invoice cascades and removes it cleanly, so it must NOT block deletion.
  // Only an EXTERNAL receipt/payment (which pushes paidAmount above the inline
  // paymentAmount via invoiceClearingHelper) would be orphaned, so block on that.
  const RETURN_TYPES = ['SR', 'NSR', 'PR', 'NPR'];
  const paidAmt = Number(transaction.paidAmount) || 0;
  const inlinePaid = Number(transaction.paymentAmount) || 0;
  const externallyCleared = Math.round((paidAmt - inlinePaid) * 100) / 100;
  // Returns are never cleared by a SEPARATE receipt/payment — their refund is
  // always entered inline on the return itself and cascades on delete. Skip this
  // guard for them (older returns lack a stored `paymentAmount`, which would
  // otherwise make `externallyCleared` equal the whole refund and wrongly block).
  // PAY (Payroll Run) is also skipped: its "payment" is a bulk Bank Payment the
  // payroll form itself deletes/reverses on delete/cancel — the invoice-style
  // lookup can't find it (wrong doc type + link), so it would only misfire here.
  if (!RETURN_TYPES.includes(transaction.tranTypeCode) && transaction.tranTypeCode !== 'PAY' && externallyCleared > 0.01) {
    // Find the separate receipt(s)/payment(s) that cleared this invoice so the
    // message can name exactly what to reverse. Sale invoices are cleared by
    // Cash/Bank Receipts; purchase invoices by Cash/Bank Payments.
    let clearingDocs = [];
    try {
      const clearTypes = transaction.partyType === 'vendor' ? ['CP', 'BP'] : ['CR', 'BR'];
      const clearQuery = query(
        collection(db, 'companies', companyId, 'transactions'),
        where('tranTypeCode', 'in', clearTypes)
      );
      const clearSnap = await getDocs(clearQuery);
      clearingDocs = clearSnap.docs
        .map(d => d.data())
        .filter(r => Array.isArray(r.invoiceAllocations) &&
          r.invoiceAllocations.some(a => a.invoiceId === transactionId && (Number(a.allocatedAmount) || 0) > 0))
        .map(r => r.docNumber)
        .filter(Boolean);
    } catch (e) { /* best-effort lookup; fall back to a generic message */ }

    const count = clearingDocs.length;
    const detail = count > 0
      ? `${count} separate receipt/payment${count > 1 ? 's' : ''} (${clearingDocs.join(', ')})`
      : `a separate receipt/payment`;
    throw new Error(
      `Cannot delete ${transaction.docNumber}: ${externallyCleared.toFixed(2)} ` +
      `has been received/paid against this invoice via ${detail}. ` +
      `Reverse or delete ${count > 1 ? 'those' : 'that'} first.`
    );
  }

  // ACC-03: parent unlink + ledger rows + stock rows + the document are removed
  // in ONE commit (document last) — never half-deleted.
  const [subIdsToDelete, stockRowsToDelete] = await Promise.all([
    listSubTransactionIds(companyId, transactionId),
    listStockFlowRows(companyId, transactionId),
  ]);
  const delWriter = createWriteCollector();
  if (transaction.parentId) {
    const parentRef = doc(db, 'companies', companyId, 'transactions', transaction.parentId);
    const parentSnap = await getDoc(parentRef);
    if (parentSnap.exists()) {
      const remaining = (parentSnap.data().childIds || []).filter(id => id !== transactionId);
      delWriter.update(parentRef, { childIds: remaining, updatedAt: new Date().toISOString() });
    }
  }
  subIdsToDelete.forEach(id => delWriter.delete(doc(db, 'companies', companyId, 'subTransactions', id)));
  stockRowsToDelete.forEach(r => delWriter.delete(doc(db, 'companies', companyId, 'stockFlow', r.id)));
  // INV-04: take this document's stock out of the items' running averages.
  if (stockRowsToDelete.length > 0) {
    await costRowsChange(companyId, delWriter, { removed: stockRowsToDelete, financialYearId: transaction.financialYearId });
  }
  delWriter.delete(transactionRef);
  if (stockRowsToDelete.length > 0) {
    delWriter.afterCommit(() => {
      applyEntriesToStockCache(companyId, stockRowsToDelete, -1);
      invalidateItemStock(companyId);
    });
  }
  await delWriter.commit();

  // ── AUDIT: record the deletion BEFORE the doc is gone forever ──
  // Every voucher delete in the app funnels through here, so this one call gives
  // the User Activity Log complete coverage of document deletions. Fire-and-forget:
  // a logging failure must never fail (or roll back) the delete itself.
  writeAuditLog(
    companyId,
    auth.currentUser?.uid || null,
    'delete',
    transaction.tranTypeCode || 'transaction',
    transactionId,
    {
      docNumber: transaction.docNumber || '',
      docDate: transaction.docDate || '',
      partyName: transaction.partyName || '',
      amount: Number(transaction.totalAmount) || 0,
      branchId: transaction.branchId || null,
      branchName: transaction.branchName || '',
      status: transaction.status || '',
    }
  ).catch(() => { /* never block a delete on logging */ });

  // Document numbers are NEVER reused (audit integrity). Deleting a document
  // leaves a permanent gap — the counter is not rolled back. This avoids
  // re-collisions when a deleted doc later returns via restore/replication.

  return { deleted: true, transactionId };
};

// Public deleteTransaction — coalesces double-click deletes on the same doc and
// shows the global "Deleting…" overlay (delete is slow: it removes the doc, its
// ledger entries and stock rows). Without this the UI froze silently for 5-8s.
const _inFlightDeletes = new Map();
export const deleteTransaction = (companyId, transactionId) => {
  const key = `${companyId}~${transactionId}`;
  if (_inFlightDeletes.has(key)) return _inFlightDeletes.get(key);
  beginBusy('Deleting…');
  const p = deleteTransactionImpl(companyId, transactionId)
    .finally(() => { _inFlightDeletes.delete(key); endBusy(); });
  _inFlightDeletes.set(key, p);
  return p;
};

/**
 * Delete a POS bill AND its linked payment(s). Unlike deleteTransaction (which
 * blocks when a separate receipt/payment cleared the invoice), this CASCADES:
 * it deletes the receipt(s)/payment(s) that were received against this bill,
 * then deletes the bill itself.
 *
 * SAFETY: a single receipt can pay several invoices. If a linked receipt also
 * covers OTHER invoices, we DON'T delete it (that would un-pay the others) —
 * we throw so the caller can tell the user to handle it manually. Bills with
 * child documents (e.g. a sale return) are also blocked.
 *
 * @returns {{deleted:true, transactionId, deletedPayments:number}}
 */
export const deletePOSBillWithPayments = async (companyId, transactionId) => {
  const ref = doc(db, 'companies', companyId, 'transactions', transactionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Bill not found');
  const txn = snap.data();

  if (txn.childIds && txn.childIds.length > 0) {
    throw new Error('Cannot delete: this bill has linked documents (e.g. a return). Delete those first.');
  }

  // Find separate receipts/payments allocated to this bill.
  const clearTypes = txn.partyType === 'vendor' ? ['CP', 'BP'] : ['CR', 'BR'];
  let linked = [];
  try {
    const qs = await getDocs(query(
      collection(db, 'companies', companyId, 'transactions'),
      where('tranTypeCode', 'in', clearTypes)
    ));
    linked = qs.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(r => Array.isArray(r.invoiceAllocations) &&
        r.invoiceAllocations.some(a => a.invoiceId === transactionId && (Number(a.allocatedAmount) || 0) > 0));
  } catch (e) { /* best-effort */ }

  // Protect other invoices: block if a linked payment also covers another invoice.
  const multi = linked.filter(r =>
    (r.invoiceAllocations || []).filter(a => (Number(a.allocatedAmount) || 0) > 0).length > 1);
  if (multi.length > 0) {
    throw new Error(
      `Payment ${multi.map(r => r.docNumber).join(', ')} also covers other invoices. ` +
      `Delete or adjust that payment manually first, then delete this bill.`
    );
  }

  // Delete the solely-linked payment(s) — reverses their accounting cleanly.
  for (const r of linked) {
    await deleteTransaction(companyId, r.id);
  }

  // Delete the bill itself (payments are gone, so no orphaned clearing remains).
  // ACC-03: bill + its ledger + stock rows removed in one commit (bill last).
  const [billSubIds, billStockRows] = await Promise.all([
    listSubTransactionIds(companyId, transactionId),
    listStockFlowRows(companyId, transactionId),
  ]);
  const billWriter = createWriteCollector();
  if (txn.parentId) {
    const parentRef = doc(db, 'companies', companyId, 'transactions', txn.parentId);
    const parentSnap = await getDoc(parentRef);
    if (parentSnap.exists()) {
      billWriter.update(parentRef, {
        childIds: (parentSnap.data().childIds || []).filter(id => id !== transactionId),
        updatedAt: new Date().toISOString()
      });
    }
  }
  billSubIds.forEach(id => billWriter.delete(doc(db, 'companies', companyId, 'subTransactions', id)));
  billStockRows.forEach(r => billWriter.delete(doc(db, 'companies', companyId, 'stockFlow', r.id)));
  // INV-04: take the bill's stock out of the items' running averages.
  if (billStockRows.length > 0) {
    await costRowsChange(companyId, billWriter, { removed: billStockRows, financialYearId: txn.financialYearId });
  }
  billWriter.delete(ref);
  if (billStockRows.length > 0) {
    billWriter.afterCommit(() => {
      applyEntriesToStockCache(companyId, billStockRows, -1);
      invalidateItemStock(companyId);
    });
  }
  await billWriter.commit();

  // AUDIT: POS bills are deleted through this cascade path, not deleteTransaction.
  writeAuditLog(
    companyId,
    auth.currentUser?.uid || null,
    'delete',
    txn.tranTypeCode || 'PS',
    transactionId,
    {
      docNumber: txn.docNumber || '',
      docDate: txn.docDate || '',
      partyName: txn.partyName || '',
      amount: Number(txn.totalAmount) || 0,
      branchId: txn.branchId || null,
      branchName: txn.branchName || '',
      status: txn.status || '',
      note: linked.length ? `also deleted ${linked.length} linked payment(s)` : '',
    }
  ).catch(() => { /* never block a delete on logging */ });

  return { deleted: true, transactionId, deletedPayments: linked.length };
};

/**
 * Get transaction by ID
 */
export const getTransaction = async (companyId, transactionId) => {
  const transactionRef = doc(db, 'companies', companyId, 'transactions', transactionId);
  const transactionDoc = await getDoc(transactionRef);

  if (!transactionDoc.exists()) {
    return null;
  }

  return {
    id: transactionDoc.id,
    ...transactionDoc.data()
  };
};

/**
 * Get transactions by criteria
 */
export const getTransactions = async (companyId, criteria = {}) => {
  const transactionsRef = collection(db, 'companies', companyId, 'transactions');
  let q = transactionsRef;

  const conditions = [];

  if (criteria.tranTypeCode) {
    conditions.push(where('tranTypeCode', '==', criteria.tranTypeCode));
  }

  if (criteria.financialYearId) {
    conditions.push(where('financialYearId', '==', criteria.financialYearId));
  }

  if (criteria.status) {
    conditions.push(where('status', '==', criteria.status));
  }

  if (criteria.partyType) {
    conditions.push(where('partyType', '==', criteria.partyType));
  }

  if (criteria.partyId) {
    conditions.push(where('partyId', '==', criteria.partyId));
  }

  if (criteria.parentId) {
    conditions.push(where('parentId', '==', criteria.parentId));
  }

  // Job scoping (2026-08-31): equality on jobId only — uses Firestore's automatic
  // single-field index, so NO composite index is required. Used by the Material
  // Issue job-cost hint to pull just one job's purchases cheaply.
  if (criteria.jobId) {
    conditions.push(where('jobId', '==', criteria.jobId));
  }

  if (criteria.branchId) {
    conditions.push(where('branchId', '==', criteria.branchId));
  }

  // Server-side date bounds (2026-07-16, Firebase cost): callers with a date
  // range pass fromDate/toDate ('YYYY-MM-DD') so Firestore returns only the
  // range instead of the whole year filtered client-side. Covered by the
  // pagination composite indexes (tranTypeCode+docDate variants). Only safe
  // for period-based lists/summaries — NEVER for cumulative reports.
  if (criteria.fromDate) {
    conditions.push(where('docDate', '>=', criteria.fromDate));
  }
  if (criteria.toDate) {
    conditions.push(where('docDate', '<=', criteria.toDate));
  }

  // Apply conditions without orderBy to avoid requiring composite indexes
  // Sort client-side instead
  if (conditions.length > 0) {
    q = query(transactionsRef, ...conditions);
  }

  const snapshot = await getDocs(q);
  recordReads(companyId, snapshot.size);

  // Map documents and sort client-side by docDate descending
  let transactions = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Opt-in: keep only the active financial year's rows. Done CLIENT-SIDE so it
  // needs no composite index, and legacy rows that were never tagged with a
  // financialYearId still show (so nothing silently disappears). Used by the
  // document-list screens (Sales, Purchases, etc.) — NOT by reports/dashboards
  // that intentionally span years, hence opt-in rather than default.
  if (criteria.activeYearOnly && !criteria.financialYearId) {
    const fyId = (typeof window !== 'undefined' && window.__AMAL_ACTIVE_FY_ID__) || null;
    if (fyId) {
      transactions = transactions.filter(t => !t.financialYearId || t.financialYearId === fyId);
    }
  }

  // Sort by docNumber descending first, then by docDate descending
  transactions.sort(docListComparator);

  return transactions;
};

// Newest-first comparator shared by the full list and the paginated pages:
// docNumber descending, then docDate descending.
const docListComparator = (a, b) => {
  const numA = parseInt((a.docNumber || '').split('-').pop()) || 0;
  const numB = parseInt((b.docNumber || '').split('-').pop()) || 0;
  if (numB !== numA) return numB - numA;
  const dateA = a.docDate || '';
  const dateB = b.docDate || '';
  return dateB.localeCompare(dateA);
};

/**
 * ── PAGINATED document lists (2026-07-16, Firebase cost) ──
 * The document-list screens used to load a tranType's FULL history on every
 * open (year filtering was client-side). These two functions replace that for
 * the default browse view: 20 docs per page, filtered and ordered by Firestore
 * itself. Search / status filters / custom sorts still use getTransactions
 * (full year) — loaded lazily only when the user actually filters.
 *
 * Requires composite indexes (firestore.indexes.json):
 *   transactions: tranTypeCode ASC + docDate DESC
 *   transactions: tranTypeCode ASC + branchId ASC + docDate DESC
 */
const buildPageConditions = ({ tranTypeCode, branchId = null, fromDate = null, toDate = null }) => {
  const conds = [where('tranTypeCode', '==', tranTypeCode)];
  if (branchId) conds.push(where('branchId', '==', branchId));
  if (fromDate) conds.push(where('docDate', '>=', fromDate));
  if (toDate) conds.push(where('docDate', '<=', toDate));
  return conds;
};

export const getTransactionsPage = async (companyId, criteria = {}) => {
  const { pageSize = 20, cursor = null } = criteria;
  const transactionsRef = collection(db, 'companies', companyId, 'transactions');
  const conds = buildPageConditions(criteria);
  const parts = [...conds, orderBy('docDate', 'desc')];
  if (cursor) parts.push(startAfter(cursor));
  parts.push(limit(pageSize));
  const snapshot = await getDocs(query(transactionsRef, ...parts));
  recordReads(companyId, snapshot.docs.length);

  const rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  rows.sort(docListComparator); // within-page ordering matches the classic list

  return {
    rows,
    lastVisible: snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length === pageSize
  };
};

export const getTransactionsCount = async (companyId, criteria = {}) => {
  const transactionsRef = collection(db, 'companies', companyId, 'transactions');
  const snap = await getCountFromServer(query(transactionsRef, ...buildPageConditions(criteria)));
  recordReads(companyId, 1); // aggregation ≈ 1 read per 1,000 matches
  return snap.data().count;
};

/** Fetch one transaction by id (report drill-down fallback when the target
 *  isn't inside the currently loaded page). */
export const getTransactionById = async (companyId, transactionId) => {
  const snap = await getDoc(doc(db, 'companies', companyId, 'transactions', transactionId));
  recordReads(companyId, 1);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Footer summary for paginated lists, aggregated SERVER-side (~1 read per
 * 1,000 matching docs instead of downloading them): per-status counts and an
 * optional outstanding-amount sum. Uses the same FY window/branch criteria as
 * the page queries. Requires composite indexes with `status` (see
 * firestore.indexes.json).
 */
export const getDocListSummary = async (companyId, criteria = {}) => {
  const { statuses = [], outstandingStatuses = [], outstandingField = 'balanceAmount' } = criteria;
  const transactionsRef = collection(db, 'companies', companyId, 'transactions');
  const base = buildPageConditions(criteria);
  const result = { counts: {}, outstanding: 0 };

  await Promise.all([
    ...statuses.map(async (s) => {
      const snap = await getCountFromServer(query(transactionsRef, ...base, where('status', '==', s)));
      result.counts[s] = snap.data().count;
    }),
    (async () => {
      if (!outstandingStatuses.length) return;
      const snap = await getAggregateFromServer(
        query(transactionsRef, ...base, where('status', 'in', outstandingStatuses)),
        { total: sum(outstandingField) }
      );
      result.outstanding = snap.data().total || 0;
    })()
  ]);

  recordReads(companyId, statuses.length + (outstandingStatuses.length ? 1 : 0));
  return result;
};

/**
 * Get ALL Opening Balance transactions for a financial year (all branches + shared)
 */
export const getAllOpeningBalanceTransactions = async (companyId, financialYearId) => {
  return await getTransactions(companyId, {
    tranTypeCode: 'OB',
    financialYearId
  });
};

/**
 * Targeted fix for the "branch added after opening was entered" case ONLY.
 * Moves the null-branch Opening Balance (transaction + its stockFlow +
 * subTransactions) onto the given branch, and removes the empty duplicate
 * branch OB that the screen auto-creates. Does nothing if there is no
 * null-branch OB. Scope is opening balances only — not a blanket migration.
 * @returns {{moved:boolean, reason?:string, counts?:object, dupRemoved?:boolean}}
 */
export const relinkOrphanOpeningToBranch = async (companyId, financialYearId, branchId, branchName) => {
  if (!companyId || !financialYearId || !branchId) {
    throw new Error('companyId, financialYearId and branchId are required');
  }
  const allOBs = await getAllOpeningBalanceTransactions(companyId, financialYearId);
  const orphan = allOBs.find(t => !t.branchId);
  if (!orphan) return { moved: false, reason: 'no-orphan' };
  const dup = allOBs.find(t => t.branchId === branchId && t.id !== orphan.id);

  const [sfRows, subRows] = await Promise.all([
    getStockFlow(companyId, orphan.id),
    getSubTransactions(companyId, orphan.id),
  ]);

  const counts = { stockFlow: 0, subTransactions: 0 };
  const patch = async (rows, collName, key) => {
    for (let i = 0; i < rows.length; i += 400) {
      const slice = rows.slice(i, i + 400);
      const batch = writeBatch(db);
      slice.forEach(r => batch.update(doc(db, 'companies', companyId, collName, r.id), {
        branchId, branchName: branchName || null, branchBackfilled: true, updatedAt: new Date().toISOString()
      }));
      await batch.commit();
      counts[key] += slice.length;
    }
  };
  await patch(sfRows, 'stockFlow', 'stockFlow');
  await patch(subRows, 'subTransactions', 'subTransactions');
  await updateDoc(doc(db, 'companies', companyId, 'transactions', orphan.id), {
    branchId, branchName: branchName || null, branchBackfilled: true, updatedAt: new Date().toISOString()
  });

  // Remove the empty duplicate branch OB (only when it carries no rows of its own).
  let dupRemoved = false;
  if (dup) {
    const [dsf, dsub] = await Promise.all([getStockFlow(companyId, dup.id), getSubTransactions(companyId, dup.id)]);
    if (dsf.length === 0 && dsub.length === 0) {
      await deleteDoc(doc(db, 'companies', companyId, 'transactions', dup.id));
      dupRemoved = true;
    }
  }
  return { moved: true, counts, dupRemoved };
};

/**
 * "Misplaced" opening lines (2026-10-09): lines tagged with a branch that sit
 * inside ANOTHER branch's opening document of the same year. They came from
 * saving / importing on Opening Balances while the screen still had the
 * previous branch's opening loaded. Reports pick lines by branch, so the
 * branch's opening was counted twice; the Opening Balances screen never showed
 * them (each screen reads only its own opening), so they could not be removed.
 */
const otherBranchOpenings = (allOBs, branchId) =>
  (allOBs || []).filter(t => (t.branchId || null) !== branchId);

const misplacedOpeningQueries = (companyId, others, branchId) => {
  const subCol = collection(db, 'companies', companyId, 'subTransactions');
  const sfCol = collection(db, 'companies', companyId, 'stockFlow');
  return others.map(o => ({
    sub: query(subCol, where('transactionId', '==', o.id), where('branchId', '==', branchId)),
    sf: query(sfCol, where('transactionId', '==', o.id), where('branchId', '==', branchId)),
  }));
};

/** How many misplaced lines this branch has — counted on the server (cheap). */
export const countMisplacedOpening = async (companyId, financialYearId, branchId, allOBs = null) => {
  if (!companyId || !financialYearId || !branchId || branchId === 'all') return { accounts: 0, stock: 0 };
  const list = allOBs || await getAllOpeningBalanceTransactions(companyId, financialYearId);
  const qs = misplacedOpeningQueries(companyId, otherBranchOpenings(list, branchId), branchId);
  let accounts = 0;
  let stock = 0;
  await Promise.all(qs.map(async ({ sub, sf }) => {
    const [a, b] = await Promise.all([getCountFromServer(sub), getCountFromServer(sf)]);
    accounts += a.data().count;
    stock += b.data().count;
  }));
  if (qs.length) recordReads(companyId, qs.length * 2);
  return { accounts, stock };
};

/** The misplaced lines themselves: { sub: [...], stock: [...] }. */
export const listMisplacedOpening = async (companyId, financialYearId, branchId, allOBs = null) => {
  if (!companyId || !financialYearId || !branchId || branchId === 'all') return { sub: [], stock: [] };
  const list = allOBs || await getAllOpeningBalanceTransactions(companyId, financialYearId);
  const qs = misplacedOpeningQueries(companyId, otherBranchOpenings(list, branchId), branchId);
  const sub = [];
  const stock = [];
  await Promise.all(qs.map(async (q) => {
    const [a, b] = await Promise.all([getDocs(q.sub), getDocs(q.sf)]);
    a.docs.forEach(d => sub.push({ id: d.id, ...d.data() }));
    b.docs.forEach(d => stock.push({ id: d.id, ...d.data() }));
  }));
  return { sub, stock };
};

/**
 * Put a branch's misplaced opening lines right. Each line that the branch's
 * own opening already has (same account / same item + location + batch) is a
 * duplicate and is removed; any other line is moved into the branch's own
 * opening, so nothing real is lost. Other branches' own lines are never touched.
 * Returns counts + the stock items affected (the caller refreshes their cost).
 */
export const repairMisplacedOpening = async (companyId, financialYearId, branchId, branchName = null) => {
  if (!companyId || !financialYearId || !branchId || branchId === 'all') {
    throw new Error('companyId, financialYearId and a specific branchId are required');
  }
  const allOBs = await getAllOpeningBalanceTransactions(companyId, financialYearId);
  const own = allOBs.find(t => t.branchId === branchId);
  if (!own) throw new Error('This branch has no opening for this year yet.');

  const result = { removed: { accounts: 0, stock: 0 }, moved: { accounts: 0, stock: 0 }, itemIds: [] };
  const { sub, stock } = await listMisplacedOpening(companyId, financialYearId, branchId, allOBs);
  if (!sub.length && !stock.length) return result;

  const [ownSub, ownSf] = await Promise.all([getSubTransactions(companyId, own.id), getStockFlow(companyId, own.id)]);
  const ownAccounts = new Set(ownSub.filter(r => r.status !== 'Cancelled').map(r => r.accountId));
  const sfKey = (r) => `${r.itemId}|${r.locationId || ''}|${String(r.batchNumber || '').trim().toLowerCase()}`;
  const ownStock = new Set(ownSf.filter(r => r.status !== 'Cancelled').map(sfKey));

  const timestamp = new Date().toISOString();
  const moveFields = (r) => ({
    transactionId: own.id,
    docNumber: own.docNumber || r.docNumber || null,
    docDate: own.docDate || r.docDate || null,
    branchName: branchName || own.branchName || r.branchName || null,
    movedFromTransactionId: r.transactionId,
    updatedAt: timestamp,
  });

  const ops = [];
  const removedStock = [];
  const itemIds = new Set();
  sub.forEach(r => {
    const ref = doc(db, 'companies', companyId, 'subTransactions', r.id);
    if (r.accountId && ownAccounts.has(r.accountId)) {
      ops.push(b => b.delete(ref));
      result.removed.accounts += 1;
    } else {
      ops.push(b => b.update(ref, moveFields(r)));
      if (r.accountId) ownAccounts.add(r.accountId);
      result.moved.accounts += 1;
    }
  });
  stock.forEach(r => {
    const ref = doc(db, 'companies', companyId, 'stockFlow', r.id);
    const key = sfKey(r);
    if (r.itemId) itemIds.add(r.itemId);
    if (ownStock.has(key)) {
      ops.push(b => b.delete(ref));
      removedStock.push(r);
      result.removed.stock += 1;
    } else {
      ops.push(b => b.update(ref, moveFields(r)));
      ownStock.add(key);
      result.moved.stock += 1;
    }
  });
  if (result.moved.accounts + result.moved.stock > 0 && own.status !== 'Posted') {
    ops.push(b => b.update(doc(db, 'companies', companyId, 'transactions', own.id), {
      status: 'Posted', postedAt: timestamp, updatedAt: timestamp,
    }));
  }

  for (let i = 0; i < ops.length; i += 400) {
    const batch = writeBatch(db);
    ops.slice(i, i + 400).forEach(fn => fn(batch));
    await batch.commit();
  }

  if (removedStock.length) {
    applyEntriesToStockCache(companyId, removedStock, -1);
    invalidateItemStock(companyId);
  }
  result.itemIds = [...itemIds];
  return result;
};

/**
 * Create or get Opening Balance transaction for a financial year
 * @param {string} companyId - Company ID
 * @param {string} financialYearId - Financial Year ID
 * @param {string} startDate - Optional start date (used if creating new OB transaction)
 * @param {string} userId - User ID for audit trail
 * @param {string} branchId - Optional branch ID for branch-specific OB
 * @param {string} branchName - Optional branch name
 */
export const ensureOpeningBalanceTransaction = async (companyId, financialYearId, startDate, userId, branchId = null, branchName = null) => {
  // Check if OB transaction already exists for this branch
  const allOBs = await getTransactions(companyId, {
    tranTypeCode: 'OB',
    financialYearId
  });

  if (branchId) {
    // Look for branch-specific OB
    const branchOB = allOBs.find(t => t.branchId === branchId);
    if (branchOB) return branchOB;
  } else {
    // No branch — look for shared OB (branchId null)
    const sharedOB = allOBs.find(t => !t.branchId);
    if (sharedOB) return sharedOB;
  }

  // Get financial year details
  const fyRef = doc(db, 'companies', companyId, 'financialYears', financialYearId);
  const fyDoc = await getDoc(fyRef);

  if (!fyDoc.exists()) {
    throw new Error('Financial year not found');
  }

  const fy = fyDoc.data();
  const docDate = startDate || fy.startDate;

  // Create new OB transaction (branch-specific if branchId provided).
  // Uses the impl directly — this can fire on page load (get-or-create), so it
  // must NOT flash the global "Saving…" overlay.
  const obTransaction = await createTransactionImpl(companyId, {
    tranTypeCode: 'OB',
    docDate: docDate,
    financialYearId,
    branchId: branchId || null,
    branchName: branchName || null,
    notes: `Opening Balance${branchName ? ' - ' + branchName : ''} for ${fy.yearName || fy.yearCode}`
  }, userId);

  // Only update FY reference if this is the shared (non-branch) OB
  if (!branchId) {
    await updateDoc(fyRef, {
      obTransactionId: obTransaction.id,
      updatedAt: new Date().toISOString()
    });
  }

  return obTransaction;
};

export default {
  generateDocNumber,
  validateTransactionDate,
  getPartyAccount,
  createTransaction,
  updateTransactionStatus,
  updateTransaction,
  deleteTransaction,
  getTransaction,
  getTransactions,
  deletePOSBillWithPayments,
  ensureOpeningBalanceTransaction,
  getAllOpeningBalanceTransactions,
  relinkOrphanOpeningToBranch,
  countMisplacedOpening,
  listMisplacedOpening,
  repairMisplacedOpening,
  orderPendingLines,
  closeOrder,
  reopenOrder
};
